import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Incident } from '../data/demoData';
import { formatDate, formatTimeAgo, sentinelIncidentNumber } from '../lib/utils';
import { apiFetch, ApiError } from '../lib/apiClient';
import { exportIncidentReportPdf, type IncidentReportPayload } from '../lib/incidentReportPdf';
import { detectIocType, type IocType } from '../lib/iocDetect';
import { UserInfoPanel } from './UserInfoPanel';
import { InvestigatePanel } from './InvestigatePanel';
import { AskCopilotButton } from './AskCopilotButton';
import { IncidentPlaybooksPanel } from './IncidentPlaybooksPanel';
import { NativeResponseActions } from './NativeResponseActions';
import { DeepInvestigationDrawer } from './DeepInvestigationDrawer';
import { IncidentEventsRuleQueryPanel } from './IncidentEventsRuleQueryPanel';
import { CaseEvidencePanel } from './CaseEvidencePanel';
import { AttackStoryPanel } from './AttackStoryPanel';
import { IncidentExplainabilityPanel } from './IncidentExplainabilityPanel';
import { IncidentWhatIfPanel } from './IncidentWhatIfPanel';
import { IncidentSlaPredictionPanel } from './IncidentSlaPredictionPanel';
import { IncidentAutopilotPanel } from './IncidentAutopilotPanel';
import { NextBestQueryPanel } from './NextBestQueryPanel';
import { CustomerUpdateGeneratorPanel } from './CustomerUpdateGeneratorPanel';
import { GuidedTriagePanel } from './GuidedTriagePanel';
import { IncidentVulnerabilityCorrelationPanel } from './IncidentVulnerabilityCorrelationPanel';
import { IncidentSimilarityPanel } from './IncidentSimilarityPanel';
import { fetchIncidentEventsRuleQuery } from '../lib/incidentEventsRuleQueryApi';
import { useAuth } from '../context/AuthContext';
import { fmtTs } from '../lib/globalTz';
import { notifyIncidentExecutionUpdated } from '../lib/incidentExecutionEvents';
import { IncidentExecutionDetailsPanel } from './IncidentExecutionDetailsPanel';

// ── Auto IOC reputation lookup (shared shape with /api/ioc/reputation) ──
type ReputationVerdict = 'malicious' | 'suspicious' | 'clean' | 'unknown';
interface ReputationProviderResult {
  provider: 'virustotal' | 'abuseipdb' | 'otx' | 'spur';
  ok: boolean;
  verdict: ReputationVerdict;
  summary?: string;
  details?: Record<string, unknown>;
  link?: string;
  error?: string;
}
interface ReputationReport {
  ioc: string;
  type: IocType;
  fromCache: boolean;
  overallVerdict: ReputationVerdict;
  malicious: number;
  suspicious: number;
  total: number;
  results: ReputationProviderResult[];
}
interface BatchReportEntry {
  ioc: string;
  ok: boolean;
  report?: ReputationReport;
  error?: string;
  code?: string;
}

const PROVIDER_LABEL: Record<ReputationProviderResult['provider'], string> = {
  virustotal: 'VirusTotal',
  abuseipdb: 'AbuseIPDB',
  otx: 'OTX AlienVault',
  spur: 'Spur',
};

// Compact label used inside the per-entity link pills so the row stays scannable.
const PROVIDER_SHORT: Record<ReputationProviderResult['provider'], string> = {
  virustotal: 'VT',
  abuseipdb: 'AB',
  otx: 'OTX',
  spur: 'Spur',
};

// Verdict-colored background classes for the per-provider link pills.
function providerPillClass(v: ReputationVerdict): string {
  switch (v) {
    case 'malicious':  return 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900';
    case 'suspicious': return 'bg-orange-100 dark:bg-orange-950 text-orange-700 dark:text-orange-300 hover:bg-orange-200 dark:hover:bg-orange-900';
    case 'clean':      return 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900';
    default:           return 'bg-muted text-muted-foreground hover:bg-muted/80';
  }
}

/** Pick one IOC value+type from an entity, or null if it isn't a lookupable IOC. */
function pickIocFromEntity(e: Entity): { value: string; type: IocType } | null {
  // Prefer SHA-256 when present on file entities.
  if (typeof e === 'object' && e.sha256 && /^[a-fA-F0-9]{64}$/.test(e.sha256)) {
    return { value: e.sha256, type: 'hash' };
  }
  const name = getEntityName(e);
  if (!name) return null;
  const explicitType = (getEntityType(e) ?? '').toLowerCase();
  // Skip account/user entities even if they happen to look like a domain (UPN local-part stripped, etc.)
  if (explicitType === 'account' || explicitType === 'user' || explicitType === 'mailmessage' || explicitType === 'malware') {
    return null;
  }
  const detected = detectIocType(name);
  if (detected === 'unknown') return null;
  return { value: name, type: detected };
}

/** Pick the more-severe verdict between two values (malicious > suspicious > clean > unknown). */
function maxVerdict(a: ReputationVerdict | undefined, b: ReputationVerdict | undefined): ReputationVerdict {
  const rank: Record<ReputationVerdict, number> = { unknown: 0, clean: 1, suspicious: 2, malicious: 3 };
  const ar = rank[a ?? 'unknown'];
  const br = rank[b ?? 'unknown'];
  return ar >= br ? (a ?? 'unknown') : (b ?? 'unknown');
}

/** Extract geo/VPN context from Spur and AbuseIPDB results in a reputation report. */
function extractIpGeo(rep: ReputationReport): {
  countryCode?: string; usageType?: string; isp?: string;
  tunnelLabel?: string; isVpn?: boolean; isTor?: boolean; isProxy?: boolean; isHosting?: boolean;
} | null {
  const abuseR = rep.results.find(r => r.provider === 'abuseipdb' && r.ok);
  const spurR  = rep.results.find(r => r.provider === 'spur' && r.ok);
  const out: { countryCode?: string; usageType?: string; isp?: string; tunnelLabel?: string; isVpn?: boolean; isTor?: boolean; isProxy?: boolean; isHosting?: boolean } = {};
  let hasAny = false;
  if (abuseR) {
    // Try structured details first
    const d = ((abuseR.details ?? {}) as { countryCode?: string; usageType?: string; isp?: string });
    if (d.countryCode) { out.countryCode = d.countryCode; hasAny = true; }
    if (d.usageType)   { out.usageType   = d.usageType;   hasAny = true; }
    if (d.isp)         { out.isp         = d.isp;         hasAny = true; }
    // Fallback: parse from summary "...% confidence, N reports • <usageType> • <CC>"
    if ((!out.countryCode || !out.usageType) && abuseR.summary) {
      const parts = abuseR.summary.split('•').map((s: string) => s.trim());
      if (parts.length >= 2) {
        const lastPart = parts[parts.length - 1];
        if (!out.countryCode && lastPart && /^[A-Z]{2}$/.test(lastPart)) {
          out.countryCode = lastPart; hasAny = true;
        }
        if (!out.usageType && parts.length >= 3) {
          const mid = parts[parts.length - 2].trim();
          if (mid && !/^\d+%/.test(mid)) { out.usageType = mid; hasAny = true; }
        }
      }
    }
    if (out.usageType && /data.?cent|hosting|transit/i.test(out.usageType)) out.isHosting = true;
  }
  if (spurR?.details) {
    const d = spurR.details as { tunnels?: { type?: string; operator?: string }[]; as?: { organization?: string } };
    const tunnels = d.tunnels ?? [];
    if (tunnels.length > 0) {
      hasAny = true;
      if (tunnels.some((t: { type?: string }) => /vpn/i.test(t.type ?? '')))   out.isVpn   = true;
      if (tunnels.some((t: { type?: string }) => /tor/i.test(t.type ?? '')))   out.isTor   = true;
      if (tunnels.some((t: { type?: string }) => /proxy/i.test(t.type ?? ''))) out.isProxy = true;
      out.tunnelLabel = tunnels.map((t: { type?: string; operator?: string }) => [t.type, t.operator].filter(Boolean).join('/')).join(', ');
    }
    if (!out.isp && d.as?.organization) { out.isp = d.as.organization; hasAny = true; }
  }
  return hasAny ? out : null;
}

/** ISO-3166 2-letter code → flag emoji. */
function countryFlag(code: string): string {
  if (!code || code.length !== 2) return '';
  const u = code.toUpperCase();
  return String.fromCodePoint(u.charCodeAt(0) + 0x1F1A5) + String.fromCodePoint(u.charCodeAt(1) + 0x1F1A5);
}

const regionNames = typeof Intl !== 'undefined' && 'DisplayNames' in Intl
  ? new Intl.DisplayNames(['en'], { type: 'region' })
  : null;

/** ISO-3166 2-letter code → full country name (falls back to the code itself). */
function countryName(code: string): string {
  if (!code || code.length !== 2) return code;
  try {
    return regionNames?.of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

interface IncidentComment {
  id: string;
  message: string;
  author: string;
  createdTime: string;
}

type IncidentStatusSelection = 'New' | 'Active';
type IncidentSeveritySelection = 'Informational' | 'Low' | 'Medium' | 'High';

interface MlIncidentScore {
  mlPriorityScore: number;
  falsePositiveLikelihood: number;
  confidence: number;
  recommendedAction: string | null;
}

interface IncidentModalProps {
  incident: Incident;
  onClose: () => void;
  onCloseIncident: () => void;
  onStatusIncident?: (status?: IncidentStatusSelection) => void;
  onSeverityIncident?: (severity?: IncidentSeveritySelection) => void;
  onAssignIncident?: () => void;
  onSyncIncident?: () => void;
  syncBusy?: boolean;
  customerId?: string;
  onRunKql?: (query: string) => void;
  onOpenIncident?: (incidentNumber: string) => void;
  /**
   * When true, renders inline (no fixed-overlay backdrop) so the component can
   * serve as a dedicated full-page incident view (used for ?incident=… deep
   * links opened in a new tab). The dismiss "×" becomes a "← Back" affordance.
   */
  fullPage?: boolean;
  /**
   * When true, hides every state-changing affordance (Close, comment
   * box, agentic Investigate panel). Used by the customer portal so read-only
   * users can still analyze and read but never accidentally try to act on
   * something they can't.
   */
  readOnly?: boolean;
  /** ML-derived priority score for this incident, rendered as a read-only chip alongside labels. */
  mlScore?: MlIncidentScore | null;
}

interface RelatedIncidentRow {
  incidentNumber: string;
  title: string;
  severity: string;
  status: string;
  createdTime: string;
  sharedAlertCount: number;
}

// ── Lightweight markdown renderer for AI investigation output ──
type MdBlock =
  | { kind: 'h'; text: string }
  | { kind: 'p'; text: string }
  | { kind: 'ul'; items: string[] }
  | { kind: 'ol'; items: string[] }
  | { kind: 'code'; lang: string; code: string };

function parseMarkdown(src: string): MdBlock[] {
  const blocks: MdBlock[] = [];
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? '';
    // Fenced code block
    const fence = line.match(/^```(\w+)?\s*$/);
    if (fence) {
      const lang = fence[1] ?? 'text';
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].match(/^```\s*$/)) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // skip closing fence
      blocks.push({ kind: 'code', lang, code: codeLines.join('\n') });
      continue;
    }
    // Heading
    const heading = line.match(/^#{1,6}\s+(.*)$/);
    if (heading) {
      blocks.push({ kind: 'h', text: heading[1] });
      i++;
      continue;
    }
    // Unordered list
    if (line.match(/^[-*]\s+/)) {
      const items: string[] = [];
      while (i < lines.length && lines[i].match(/^[-*]\s+/)) {
        items.push(lines[i].replace(/^[-*]\s+/, ''));
        i++;
      }
      blocks.push({ kind: 'ul', items });
      continue;
    }
    // Ordered list
    if (line.match(/^\d+\.\s+/)) {
      const items: string[] = [];
      while (i < lines.length && lines[i].match(/^\d+\.\s+/)) {
        items.push(lines[i].replace(/^\d+\.\s+/, ''));
        i++;
      }
      blocks.push({ kind: 'ol', items });
      continue;
    }
    // Blank line
    if (!line.trim()) { i++; continue; }
    // Paragraph (collect consecutive non-special lines)
    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !lines[i].match(/^#{1,6}\s+/) &&
      !lines[i].match(/^[-*]\s+/) &&
      !lines[i].match(/^\d+\.\s+/) &&
      !lines[i].match(/^```/)
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    if (paraLines.length) blocks.push({ kind: 'p', text: paraLines.join(' ') });
  }
  return blocks;
}

function renderInline(text: string): React.ReactNode[] {
  // Handles **bold**, *italic*, `code` — applied in that order
  const parts: React.ReactNode[] = [];
  const regex = /(\*\*[^*]+\*\*|\*[^*\n]+\*|`[^`\n]+`)/g;
  let lastIdx = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIdx) parts.push(text.slice(lastIdx, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) parts.push(<strong key={key++}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith('`')) parts.push(<code key={key++} className="bg-muted text-foreground rounded px-1 py-0.5 font-mono text-[11px]">{tok.slice(1, -1)}</code>);
    else parts.push(<em key={key++} className="text-muted-foreground">{tok.slice(1, -1)}</em>);
    lastIdx = regex.lastIndex;
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx));
  return parts;
}

function MarkdownView({ blocks, onRunKql }: { blocks: MdBlock[]; onRunKql?: (q: string) => void }) {
  return (
    <div className="space-y-3 text-sm text-foreground">
      {blocks.map((b, i) => {
        if (b.kind === 'h') return <h4 key={i} className="font-semibold text-foreground mt-3 first:mt-0 text-sm border-b border-border pb-1">{b.text}</h4>;
        if (b.kind === 'p') return <p key={i} className="leading-relaxed">{renderInline(b.text)}</p>;
        if (b.kind === 'ul') return (
          <ul key={i} className="list-disc list-outside ml-5 space-y-1">
            {b.items.map((it, j) => <li key={j} className="leading-relaxed">{renderInline(it)}</li>)}
          </ul>
        );
        if (b.kind === 'ol') return (
          <ol key={i} className="list-decimal list-outside ml-5 space-y-1">
            {b.items.map((it, j) => <li key={j} className="leading-relaxed">{renderInline(it)}</li>)}
          </ol>
        );
        if (b.kind === 'code') {
          const isKql = b.lang.toLowerCase() === 'kql';
          return (
            <div key={i} className="rounded-lg border border-border overflow-hidden bg-muted/40">
              <div className="flex items-center justify-between px-3 py-1.5 bg-muted/60 border-b border-border">
                <span className="text-[10px] font-mono uppercase text-muted-foreground tracking-wider">{b.lang}</span>
                <div className="flex gap-1.5">
                  {isKql && onRunKql && (
                    <button
                      onClick={() => onRunKql(b.code)}
                      className="text-[11px] bg-primary text-primary-foreground px-2 py-0.5 rounded hover:opacity-90 transition-opacity font-semibold"
                    >
                      ▶ Run in KQL Console
                    </button>
                  )}
                  <button
                    onClick={() => navigator.clipboard.writeText(b.code).catch(() => null)}
                    className="text-[11px] border border-border bg-background text-muted-foreground px-2 py-0.5 rounded hover:bg-muted transition-colors"
                  >
                    Copy
                  </button>
                </div>
              </div>
              <pre className="px-3 py-2 text-xs font-mono text-foreground whitespace-pre-wrap break-all overflow-x-auto">{b.code}</pre>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

function effectiveStatus(i: Incident) {
  const s = (i.status || '').toLowerCase();
  if (s === 'active') {
    const owner = (i.assignedTo || '').trim();
    if (!owner || owner === 'unassigned') return 'new';
    return 'active';
  }
  return s;
}

function formatLabel(s: string | undefined): string {
  if (!s || s === 'unknown') return '-';
  return s
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, c => c.toUpperCase())
    .trim();
}

type Entity = string | { type: string; name: string; verdict?: string; sha256?: string };

interface TimelineEntry {
  id: string;
  title: string;
  severity: string;
  status?: string;
  productName: string;
  createdDateTime: string;
  description?: string;
  mitreTechniques?: string[];
  alertWebUrl?: string;
}

function escapeKqlString(raw: string): string {
  // Strip control chars (incl. CR/LF/tab/null) before escaping backslashes & quotes
  // so an attacker-controlled entity value can't break out of the string literal.
  return raw
    .replace(/[\x00-\x1F\x7F]/g, '')
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"');
}

function buildEntityHuntKql(e: Entity): string {
  const name = getEntityName(e);
  const sha = typeof e === 'object' ? e.sha256 : undefined;
  // Prefer SHA-256 only when it's actually a 64-char hex hash; otherwise fall back to name.
  const validSha = sha && /^[A-Fa-f0-9]{64}$/.test(sha) ? sha : undefined;
  const rawValue = validSha || name || '';
  const value = escapeKqlString(rawValue);
  return [
    'SecurityAlert',
    '| where TimeGenerated > ago(30d)',
    `| where Entities has "${value}"`,
    '| project TimeGenerated, DisplayName, AlertSeverity, ProductName, Description',
    '| order by TimeGenerated desc',
    '| take 100',
  ].join('\n');
}

function severityChipClass(s: string): string {
  const v = (s || '').toLowerCase();
  if (v === 'high') return 'bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300';
  if (v === 'critical' || v === 'severe') return 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300';
  if (v === 'medium') return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300';
  if (v === 'low') return 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300';
  if (v === 'informational' || v === 'info') return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400';
  return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400';
}

function getEntityName(e: Entity): string {
  return typeof e === 'string' ? e : e.name;
}

function getEntityKey(e: Entity, i: number): string {
  return `${i}-${typeof e === 'string' ? e : e.name}`;
}

function getEntityVerdict(e: Entity): string | undefined {
  if (typeof e === 'object') return e.verdict;
  return undefined;
}

function getEntityType(e: Entity): string | undefined {
  if (typeof e === 'object') return e.type;
  return undefined;
}

// Filter for entity values strong enough to pivot on without flooding results.
// Accepts hashes, IPs, emails/UPNs, GUIDs, FQDNs (>=2 dots), and reasonably long values (>=6 chars).
function isStrongEntityValue(v: unknown): v is string {
  if (typeof v !== 'string') return false;
  const s = v.trim();
  if (!s) return false;
  if (/^[A-Fa-f0-9]{32,}$/.test(s)) return true;                          // md5/sha1/sha256
  if (/^\d{1,3}(?:\.\d{1,3}){3}$/.test(s)) return true;                   // ipv4
  if (s.includes(':') && /^[A-Fa-f0-9:]{5,}$/.test(s)) return true;       // ipv6-ish
  if (s.includes('@')) return true;                                       // email/UPN
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)) return true; // GUID
  if ((s.match(/\./g)?.length ?? 0) >= 2) return true;                    // FQDN-ish
  return s.length >= 6;
}


function incidentProviderLabel(incident: Incident): string {
  const raw = (incident.providerName || incident.providerProductName || '').trim();
  if (!raw) return 'Microsoft Sentinel';
  const lower = raw.toLowerCase();
  if (lower.includes('xdr') || lower.includes('defender') || lower.includes('microsoft 365')) return 'Microsoft Defender XDR';
  if (lower.includes('sentinel') || lower.includes('securityinsights')) return 'Microsoft Sentinel';
  return raw;
}

function providerBadgeClass(provider: string): string {
  const p = provider.toLowerCase();
  if (p.includes('defender') || p.includes('xdr')) return 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800';
  if (p.includes('sentinel')) return 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800';
  if (p.includes('azure')) return 'bg-sky-100 text-sky-700 border-sky-200 dark:bg-sky-950 dark:text-sky-300 dark:border-sky-800';
  return 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-900 dark:text-slate-300 dark:border-slate-700';
}

function normaliseIncidentLabels(incident: Incident): string[] {
  const raw = Array.isArray(incident.labels) ? incident.labels : Array.isArray(incident.tags) ? incident.tags : [];
  return Array.from(new Set(raw.map(x => String(x ?? '').replace(/[<>\r\n\t]/g, ' ').replace(/\s{2,}/g, ' ').trim()).filter(Boolean))).slice(0, 30);
}

function buildAlertsQuery(incidentNumber: string): string {
  const n = Number(incidentNumber);
  const safeIncident = incidentNumber.replace(/"/g, '');
  const whereIncident = Number.isFinite(n) ? `IncidentNumber == ${n}` : `tostring(IncidentNumber) == "${safeIncident}"`;
  return [
    '// Alerts linked to the selected Microsoft Sentinel incident.',
    '// Uses AlertIds from SecurityIncident and then resolves matching SecurityAlert rows.',
    'let inc = SecurityIncident',
    '  | where TimeGenerated > ago(90d)',
    `  | where ${whereIncident}`,
    '  | summarize arg_max(TimeGenerated, *) by IncidentNumber',
    '  | extend AlertIdsRaw = todynamic(column_ifexists("AlertIds", "[]"))',
    '  | mv-expand AlertId = AlertIdsRaw to typeof(string)',
    '  | project AlertId = tostring(AlertId);',
    'SecurityAlert',
    '| where TimeGenerated > ago(90d)',
    '| where SystemAlertId in (inc) or tostring(column_ifexists("SystemAlertId", "")) in (inc)',
    '| summarize arg_max(TimeGenerated, *) by SystemAlertId',
    '| extend AlertTitle = tostring(coalesce(column_ifexists("AlertName", ""), column_ifexists("DisplayName", ""), column_ifexists("Title", "")))',
    '| project TimeGenerated, AlertTitle, AlertSeverity, ProviderName, ProductName, Status, Description, Entities, AlertLink, SystemAlertId',
    '| order by TimeGenerated desc',
  ].join('\n');
}

function cleanEntityForKql(raw: string): string {
  return raw
    .replace(/[\x00-\x1F\x7F]/g, ' ')
    .replace(/["'`\\]/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function extractWorkflowCandidates(incident: Incident, entities: Entity[]): string[] {
  const candidates: string[] = [];
  const title = cleanEntityForKql(incident.title || '');
  if (title) {
    candidates.push(title);
    const beforeFailed = title.replace(/\s+has\s+failed.*$/i, '').trim();
    if (beforeFailed && beforeFailed !== title) candidates.push(beforeFailed);
    const withoutPrefix = beforeFailed.replace(/^[_\-\s]+/, '').trim();
    if (withoutPrefix && withoutPrefix !== beforeFailed) candidates.push(withoutPrefix);
  }
  for (const e of entities) {
    const v = cleanEntityForKql(getEntityName(e));
    if (v) candidates.push(v);
  }
  return Array.from(new Set(candidates.filter(v => v.length >= 3))).slice(0, 12);
}

function buildLogicAppEventsQuery(incident: Incident, entities: Entity[]): string {
  const candidates = extractWorkflowCandidates(incident, entities);
  const quoted = candidates.map(v => `"${v.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`).join(', ');
  return [
    '// Logic App failed-run events for this Sentinel incident.',
    '// This mirrors the underlying analytics-rule style query instead of using a broad union * search.',
    `let _ks_query_now = datetime(${new Date().toISOString()});`,
    `let workflowCandidates = dynamic([${quoted}]);`,
    'AzureDiagnostics',
    "| where TimeGenerated between ((_ks_query_now - 7d) .. _ks_query_now)",
    "| where status_s =~ 'failed'",
    "| where OperationName =~ 'Microsoft.Logic/workflows/workflowRunCompleted'",
    "| where isnotempty(resource_workflowName_s)",
    "| where array_length(workflowCandidates) == 0",
    "   or tostring(resource_workflowName_s) has_any (workflowCandidates)",
    "   or tostring(resource_originRunId_s) has_any (workflowCandidates)",
    "   or tostring(_ResourceId) has_any (workflowCandidates)",
    '| project',
    '    TimeGenerated,',
    '    LogicApp = resource_workflowName_s,',
    '    RunId = resource_originRunId_s,',
    '    EventTime = endTime_t,',
    '    Error = strcat("Error Code: ", error_code_s, ", Error Message: ", error_message_s),',
    '    ResourceId = _ResourceId,',
    '    Status = status_s',
    '| order by TimeGenerated desc',
    '| take 200',
  ].join('\n');
}

function buildGenericEvidenceEventsQuery(entities: Entity[]): string {
  const values = Array.from(new Set(entities.map(getEntityName).map(cleanEntityForKql).filter(isStrongEntityValue))).slice(0, 10);
  if (values.length === 0) {
    return [
      '// No strong entity values were available for an event pivot.',
      'SecurityAlert',
      '| where TimeGenerated > ago(7d)',
      '| order by TimeGenerated desc',
      '| take 100',
    ].join('\n');
  }
  const quoted = values.map(v => `"${v.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`).join(', ');
  return [
    '// Entity-based event pivot for the selected incident.',
    '// Uses a curated list of common tables instead of union * so the query stays parser-safe.',
    `let entityValues = dynamic([${quoted}]);`,
    'union isfuzzy=true withsource=SourceTable',
    '    AzureDiagnostics,',
    '    AzureActivity,',
    '    SecurityAlert,',
    '    SigninLogs,',
    '    AADNonInteractiveUserSignInLogs,',
    '    AuditLogs,',
    '    OfficeActivity,',
    '    CommonSecurityLog,',
    '    Syslog,',
    '    SecurityEvent,',
    '    DeviceInfo,',
    '    DeviceProcessEvents,',
    '    DeviceNetworkEvents,',
    '    DeviceLogonEvents,',
    '    DeviceFileEvents,',
    '    EmailEvents,',
    '    UrlClickEvents',
    '| where TimeGenerated > ago(7d)',
    '| extend MatchedEntityValues = entityValues',
    '| where tostring(pack_all()) has_any (entityValues)',
    '| project TimeGenerated, SourceTable, Summary = substring(tostring(pack_all()), 0, 2000), MatchedEntityValues',
    '| order by TimeGenerated desc',
    '| take 200',
  ].join('\n');
}

function buildEntityEventsQuery(entities: Entity[], incident?: Incident): string {
  const title = (incident?.title || '').toLowerCase();
  const entityText = entities.map(getEntityName).join(' ').toLowerCase();
  const looksLikeLogicApp = title.includes('logic') || title.includes('workflow') || /\bhas failed\b/.test(title) || /\b(recurrence|initialize_|workflow|runid)\b/.test(entityText);
  if (incident && looksLikeLogicApp) {
    return buildLogicAppEventsQuery(incident, entities);
  }
  return buildGenericEvidenceEventsQuery(entities);
}

function buildIncidentEntitiesQuery(incidentNumber: string): string {
  const n = Number(incidentNumber);
  const safeIncident = incidentNumber.replace(/"/g, '');
  const whereIncident = Number.isFinite(n) ? `IncidentNumber == ${n}` : `tostring(IncidentNumber) == "${safeIncident}"`;
  return [
    '// Entities linked to the selected Microsoft Sentinel incident.',
    '// Pulls entities from SecurityIncident when available and from linked SecurityAlert rows.',
    'let inc = SecurityIncident',
    '  | where TimeGenerated > ago(90d)',
    `  | where ${whereIncident}`,
    '  | summarize arg_max(TimeGenerated, *) by IncidentNumber',
    '  | extend AlertIdsRaw = todynamic(column_ifexists("AlertIds", "[]"))',
    '  | mv-expand AlertId = AlertIdsRaw to typeof(string)',
    '  | project AlertId = tostring(AlertId);',
    'let incidentEntities = SecurityIncident',
    '  | where TimeGenerated > ago(90d)',
    `  | where ${whereIncident}`,
    '  | summarize arg_max(TimeGenerated, *) by IncidentNumber',
    '  | extend EntitiesRaw = todynamic(column_ifexists("Entities", "[]"))',
    '  | mv-expand Entity = EntitiesRaw',
    '  | extend EntityType = tostring(coalesce(Entity.Type, Entity.Kind, Entity.entityType, Entity.TypeName))',
    '  | extend EntityName = tostring(coalesce(Entity.Name, Entity.DisplayName, Entity.HostName, Entity.Address, Entity.UPN, Entity.UserPrincipalName, Entity.AccountName, Entity.DnsDomain, Entity.FileName, Entity.FileHash, Entity.Value, Entity))',
    '  | where isnotempty(EntityName)',
    '  | project TimeGenerated, Source = "SecurityIncident", IncidentNumber, EntityType, EntityName, Entity;',
    'let alertEntities = SecurityAlert',
    '  | where TimeGenerated > ago(90d)',
    '  | where SystemAlertId in (inc)',
    '  | extend EntitiesRaw = todynamic(column_ifexists("Entities", "[]"))',
    '  | mv-expand Entity = EntitiesRaw',
    '  | extend EntityType = tostring(coalesce(Entity.Type, Entity.Kind, Entity.entityType, Entity.TypeName))',
    '  | extend EntityName = tostring(coalesce(Entity.Name, Entity.DisplayName, Entity.HostName, Entity.Address, Entity.UPN, Entity.UserPrincipalName, Entity.AccountName, Entity.DnsDomain, Entity.FileName, Entity.FileHash, Entity.Value, Entity))',
    '  | where isnotempty(EntityName)',
    '  | project TimeGenerated, Source = "SecurityAlert", IncidentNumber = "", EntityType, EntityName, Entity;',
    'union isfuzzy=true incidentEntities, alertEntities',
    '| summarize arg_max(TimeGenerated, *) by Source, EntityType, EntityName',
    '| order by EntityType asc, EntityName asc',
  ].join('\n');
}


function verdictClass(v: string | undefined) {
  if (!v) return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400';
  if (v === 'malicious') return 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300';
  if (v === 'suspicious') return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300';
  if (v === 'clean') return 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300';
  return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400';
}

// ── Live streaming investigation event types ──
//
// Mirrors the server-side `InvestigationStreamEvent` discriminated union from
// `incidentAutoInvestigator.ts`, plus two transport-level frames the SSE route
// adds (`open` for handshake, `ping` for keepalive). Re-declared here so the
// dashboard doesn't import server code (which would cross artifact boundaries
// and pull in node-only deps).
type StreamEvidenceEvent =
  | { kind: 'plan'; totalQueries: number; entities: { type: string; value: string }[]; timeRange: { startUtc: string; endUtc: string } }
  | { kind: 'query_start'; idx: number; total: number; label: string; query: string }
  | { kind: 'query_done'; idx: number; total: number; label: string; status: 'ok' | 'empty' | 'error'; rowCount: number; error?: string }
  | { kind: 'summary'; totals: { queries: number; rows: number; errors: number } };

type StreamAgentEvent =
  | { kind: 'user'; text: string; at: string }
  | { kind: 'assistant'; text: string; at: string }
  | { kind: 'tool_call'; name: string; destructive: boolean; args: Record<string, unknown>; at: string; toolCallId: string }
  | { kind: 'tool_result'; toolCallId: string; name: string; resultPreview: string; at: string }
  | { kind: 'denied'; toolCallId: string; name: string; reason?: string; at: string }
  | { kind: 'stopped'; reason: string; at: string }
  | { kind: 'sentinel_comment'; status: 'posted' | 'skipped' | 'failed'; method?: string; reason?: string; at: string };

type StreamVerdict = {
  verdict: 'TRUE POSITIVE' | 'FALSE POSITIVE' | 'BENIGN POSITIVE';
  confidence: 'Low' | 'Medium' | 'High';
  rationale: string;
};

type InvestigationStreamFrame =
  | { kind: 'open' }
  | { kind: 'ping' }
  | { kind: 'cached'; rowId: string; signature: string; summary: string; verdict: StreamVerdict | null }
  | { kind: 'phase'; phase: 'evidence_collecting' | 'agent_thinking' | 'posting' | 'done'; at: string }
  | { kind: 'evidence'; event: StreamEvidenceEvent; at: string }
  | { kind: 'agent'; event: StreamAgentEvent }
  | { kind: 'result'; status: string; rowId: string; signature: string; summary: string | null; verdict: StreamVerdict | null; error: string | null }
  | { kind: 'error'; message: string };

// Drop frames that aren't useful in the activity feed — keepalives and the
// initial open handshake. Everything else is rendered as a row.
function isFeedWorthy(f: InvestigationStreamFrame): boolean {
  return f.kind !== 'ping' && f.kind !== 'open';
}

// Compact, scannable label for each event row in the live feed.
// Icons are intentionally bullet/text-only (no emoji) — the colour tone
// already encodes severity and a clean type-set feed reads better than
// a wall of emoji.
function feedEventLabel(f: InvestigationStreamFrame): { icon: string; title: string; tone: string } {
  if (f.kind === 'phase') {
    if (f.phase === 'evidence_collecting') return { icon: '›', title: 'Pre-gathering evidence (KQL)', tone: 'text-sky-700 dark:text-sky-300' };
    if (f.phase === 'agent_thinking') return { icon: '›', title: 'Agent reasoning over evidence', tone: 'text-indigo-700 dark:text-indigo-300' };
    if (f.phase === 'posting') return { icon: '›', title: 'Posting RCA to Sentinel', tone: 'text-violet-700 dark:text-violet-300' };
    return { icon: '✓', title: 'Investigation complete', tone: 'text-emerald-700 dark:text-emerald-300' };
  }
  if (f.kind === 'evidence') {
    const ev = f.event;
    if (ev.kind === 'plan') {
      const ents = ev.entities?.length ?? 0;
      return { icon: '›', title: `Planned ${ev.totalQueries} evidence queries across ${ents} entit${ents === 1 ? 'y' : 'ies'}`, tone: 'text-sky-700 dark:text-sky-300' };
    }
    if (ev.kind === 'query_start') return { icon: '·', title: `[${ev.idx}/${ev.total}] ${ev.label}`, tone: 'text-sky-600 dark:text-sky-400' };
    if (ev.kind === 'query_done') {
      if (ev.status === 'error') {
        return { icon: '!', title: `[${ev.idx}/${ev.total}] ${ev.label} - ${(ev.error ?? 'error').slice(0, 80)}`, tone: 'text-amber-700 dark:text-amber-300' };
      }
      if (ev.status === 'empty') {
        return { icon: '∅', title: `[${ev.idx}/${ev.total}] ${ev.label} - no rows`, tone: 'text-slate-600 dark:text-slate-400' };
      }
      return { icon: '✓', title: `[${ev.idx}/${ev.total}] ${ev.label} - ${ev.rowCount} row${ev.rowCount === 1 ? '' : 's'}`, tone: 'text-emerald-700 dark:text-emerald-300' };
    }
    // summary
    const t = ev.totals ?? { queries: 0, rows: 0, errors: 0 };
    return { icon: '›', title: `Evidence pack: ${t.queries} queries, ${t.rows} rows, ${t.errors} error${t.errors === 1 ? '' : 's'}`, tone: 'text-slate-700 dark:text-slate-300' };
  }
  if (f.kind === 'agent') {
    const ev = f.event;
    if (ev.kind === 'tool_call') {
      // OSINT/IOC lookups, KQL, hunting steps — call them out by name.
      const pretty = ev.name.replace(/_/g, ' ');
      return { icon: ev.destructive ? '!' : '›', title: `Tool call: ${pretty}`, tone: ev.destructive ? 'text-red-700 dark:text-red-300' : 'text-violet-700 dark:text-violet-300' };
    }
    if (ev.kind === 'tool_result') return { icon: '·', title: `Result from ${ev.name.replace(/_/g, ' ')}`, tone: 'text-violet-600 dark:text-violet-400' };
    if (ev.kind === 'assistant') return { icon: '›', title: 'Agent response', tone: 'text-indigo-700 dark:text-indigo-300' };
    if (ev.kind === 'denied') return { icon: '!', title: `Denied destructive tool: ${ev.name}`, tone: 'text-red-700 dark:text-red-300' };
    if (ev.kind === 'stopped') return { icon: '·', title: `Stopped: ${ev.reason}`, tone: 'text-slate-700 dark:text-slate-300' };
    if (ev.kind === 'sentinel_comment') return ev.status === 'posted'
      ? { icon: '✓', title: 'Posted RCA comment to Sentinel', tone: 'text-emerald-700 dark:text-emerald-300' }
      : { icon: '!', title: `Sentinel post ${ev.status}${ev.reason ? `: ${ev.reason}` : ''}`, tone: 'text-amber-700 dark:text-amber-300' };
    return { icon: '·', title: ev.kind, tone: 'text-slate-600 dark:text-slate-400' };
  }
  if (f.kind === 'cached') return { icon: '✓', title: 'Loaded cached investigation', tone: 'text-emerald-700 dark:text-emerald-300' };
  if (f.kind === 'result') return f.status === 'posted'
    ? { icon: '✓', title: 'Final verdict ready', tone: 'text-emerald-700 dark:text-emerald-300' }
    : { icon: '!', title: `Investigation ${f.status}`, tone: 'text-amber-700 dark:text-amber-300' };
  if (f.kind === 'error') return { icon: '!', title: `Stream error: ${f.message}`, tone: 'text-red-700 dark:text-red-300' };
  return { icon: '·', title: 'event', tone: 'text-slate-600 dark:text-slate-400' };
}

// Pull a one-line preview of a tool's arguments — KQL queries get a code
// preview, IP/hash IOCs get the bare value, everything else is JSON-truncated.
function previewToolArgs(name: string, args: Record<string, unknown>): string | null {
  const lname = name.toLowerCase();
  if (typeof args['query'] === 'string' && (lname.includes('kql') || lname.includes('log') || lname.includes('hunt'))) {
    return String(args['query']);
  }
  for (const k of ['ip', 'ioc', 'hash', 'sha256', 'md5', 'domain', 'host', 'upn', 'email', 'value']) {
    if (typeof args[k] === 'string' && args[k]) return String(args[k]);
  }
  try {
    const json = JSON.stringify(args);
    return json.length > 200 ? json.slice(0, 200) + '…' : json;
  } catch {
    return null;
  }
}

/**
 * Live feed of the SOC L1 agent's work as it happens — KQL queries, OSINT
 * lookups, agent thinking, evidence row counts. Streamed from the SSE
 * `/auto-investigate/stream` endpoint and rendered chronologically. Auto-
 * scrolls so the freshest event is always visible.
 */
function LiveInvestigationFeed({
  events,
  phase,
  active,
  error,
  cached,
}: {
  events: InvestigationStreamFrame[];
  phase: 'evidence_collecting' | 'agent_thinking' | 'posting' | 'done' | null;
  active: boolean;
  error: string | null;
  cached: boolean;
}) {
  const visible = events.filter(isFeedWorthy);
  // When the backend returns a cached posted run, no live work happens — the
  // panel would otherwise vanish abruptly after "starting…", which feels like
  // a crash. Show an explicit "cached" heading so the user understands the
  // verdict rendered below comes from a previous identical-signature run.
  const heading = cached
    ? 'SOC L1 Auto-Investigation - cached result loaded'
    : phase === 'done'
      ? 'SOC L1 Auto-Investigation - complete'
      : phase === 'posting'
        ? 'SOC L1 Auto-Investigation - posting RCA…'
        : phase === 'agent_thinking'
          ? 'SOC L1 Auto-Investigation - reasoning…'
          : phase === 'evidence_collecting'
            ? 'SOC L1 Auto-Investigation - collecting evidence…'
            : active
              ? 'SOC L1 Auto-Investigation - starting…'
              : 'SOC L1 Auto-Investigation';
  return (
    <div className="rounded-lg border border-indigo-200 dark:border-indigo-900 bg-indigo-50/40 dark:bg-indigo-950/20 p-3 mb-4" aria-busy={active}>
      <div className="flex items-center gap-2 mb-2">
        <p className="text-xs font-semibold text-indigo-800 dark:text-indigo-200">{heading}</p>
        {active && (
          <span className="inline-flex h-2 w-2 rounded-full bg-indigo-500 animate-pulse" aria-hidden />
        )}
        <span className="ml-auto text-[11px] text-muted-foreground">{visible.length} event{visible.length === 1 ? '' : 's'}</span>
      </div>
      {error && (
        <div className="mb-2 text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded px-2 py-1.5 whitespace-pre-wrap break-words font-mono">
          {error}
        </div>
      )}
      {cached && (
        <div className="mb-2 text-xs text-emerald-800 dark:text-emerald-200 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900 rounded px-2 py-1.5">
          A previous SOC L1 Auto-Investigation run for this exact incident signature was reused - no LLM call was made. The verdict and full RCA are rendered in the <strong>AI Investigation</strong> panel below. Click <strong>Re-analyze with AI</strong> to force a fresh run.
        </div>
      )}
      <div className="max-h-[260px] overflow-y-auto space-y-1.5 pr-1">
        {visible.length === 0 && active && (
          <div className="text-xs text-muted-foreground italic">Waiting for first event…</div>
        )}
        {visible.map((f, i) => {
          const { icon, title, tone } = feedEventLabel(f);
          // For tool calls, show the args preview as a monospace code line.
          let preview: string | null = null;
          if (f.kind === 'agent' && f.event.kind === 'tool_call') {
            preview = previewToolArgs(f.event.name, f.event.args);
          } else if (f.kind === 'agent' && f.event.kind === 'tool_result') {
            preview = f.event.resultPreview ? f.event.resultPreview.slice(0, 200) : null;
          } else if (f.kind === 'agent' && f.event.kind === 'assistant') {
            preview = f.event.text.length > 200 ? f.event.text.slice(0, 200) + '…' : f.event.text;
          } else if (f.kind === 'evidence' && f.event.kind === 'query_start') {
            preview = f.event.query.length > 240 ? f.event.query.slice(0, 240) + '…' : f.event.query;
          }
          return (
            <div key={i} className="text-xs">
              <div className={`flex items-baseline gap-1.5 ${tone}`}>
                <span className="shrink-0" aria-hidden>{icon}</span>
                <span className="font-medium break-words">{title}</span>
              </div>
              {preview && (
                <pre className="mt-0.5 ml-5 text-[11px] font-mono whitespace-pre-wrap break-all bg-card border border-border rounded px-2 py-1 text-foreground/80">
                  {preview}
                </pre>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Renders the autonomous SOC L1 investigation result for an incident — the
 * same Markdown report the scheduler also posted to Sentinel as a comment.
 * Visible to all roles (read-only) so customer-portal users see the analysis
 * without being able to re-trigger a costly LLM run themselves. The status
 * pill summarises the run state when the body isn't ready yet.
 */
function AutoInvestigationSection({
  ai,
}: {
  ai: NonNullable<Incident['autoInvestigation']>;
}) {
  const blocks = useMemo(() => (ai.summary ? parseMarkdown(ai.summary) : []), [ai.summary]);
  const finished = ai.finishedAt ? fmtTs(ai.finishedAt) : '';
  const tone =
    ai.status === 'posted'
      ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900'
      : ai.status === 'completed'
        ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900'
      : ai.status === 'failed'
        ? 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900'
        : 'bg-sky-50 dark:bg-sky-950/30 border-sky-200 dark:border-sky-900';
  const labelTone =
    ai.status === 'posted'
      ? 'text-emerald-700 dark:text-emerald-300'
      : ai.status === 'completed'
        ? 'text-amber-700 dark:text-amber-300'
      : ai.status === 'failed'
        ? 'text-red-700 dark:text-red-300'
        : 'text-sky-700 dark:text-sky-300';
  const heading =
    ai.status === 'posted'
      ? 'SOC L1 Auto-Investigation (posted to Sentinel)'
      : ai.status === 'completed'
        ? 'SOC L1 Auto-Investigation (complete · Sentinel comment not posted)'
      : ai.status === 'running'
        ? 'SOC L1 Auto-Investigation (in progress…)'
        : ai.status === 'pending'
          ? 'SOC L1 Auto-Investigation (queued)'
          : ai.status === 'failed'
            ? 'SOC L1 Auto-Investigation (failed)'
            : 'SOC L1 Auto-Investigation';

  return (
    <div className={`rounded-lg p-3 mb-4 border ${tone}`} aria-busy={ai.status === 'running' || ai.status === 'pending'}>
      <div className="flex items-center gap-2 mb-2">
        <p className={`text-xs font-semibold ${labelTone}`}>{heading}</p>
        {finished && (
          <span className="text-[11px] text-muted-foreground">{finished}</span>
        )}
      </div>
      {ai.status === 'completed' && ai.error && (
        <div className="mb-2 rounded border border-amber-300/60 bg-amber-100/40 px-2 py-1.5 text-[11px] text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          Analysis completed successfully. Sentinel comment delivery warning: {ai.error}
        </div>
      )}
      {(ai.status === 'running' || ai.status === 'pending') && !ai.summary && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="animate-pulse">●</span>
          <span>Autonomous L3 analyst is running KQL + OSINT enrichment. Typically 20–90 seconds.</span>
        </div>
      )}
      {ai.summary && (
        <div className="mt-2 bg-card border border-border rounded-lg p-3 max-h-[600px] overflow-y-auto">
          <MarkdownView blocks={blocks} />
        </div>
      )}
      {ai.status === 'failed' && !ai.summary && (
        <p className="text-xs text-red-700 dark:text-red-300">
          The auto-investigator could not produce a report for this incident. Check the API server logs for details, or re-run from the standard Investigate panel below.
        </p>
      )}
    </div>
  );
}

export function IncidentModal({ incident, onClose, onCloseIncident, onStatusIncident, onSeverityIncident, onAssignIncident, onSyncIncident, syncBusy = false, customerId, onRunKql, onOpenIncident, fullPage = false, readOnly = false, mlScore = null }: IncidentModalProps) {
  // SOC L1 cannot run playbooks — hide the IncidentPlaybooksPanel entirely.
  // Customer-portal users see it as informational-only via `readOnly`, which
  // is set by the parent that opens the modal.
  const { user, canCapability } = useAuth();
  const canSeePlaybooksPanel = user?.role !== 'soc_l1';
  const canComment = !readOnly && canCapability('incidents', 'comment');
  const canCloseIncident = !readOnly && canCapability('incidents', 'close');
  const canReopenIncident = !readOnly && canCapability('incidents', 'reopen');
  const canChangeIncidentStatus = !readOnly && canCapability('incidents', 'change_status');
  const canChangeIncidentSeverity = !readOnly && canCapability('incidents', 'change_severity');
  const canManageIncidentStatus = canReopenIncident || canChangeIncidentStatus;
  const canRunAiInvestigation = !readOnly && canCapability('incidents', 'run_ai_investigation');
  const canGenerateExecutiveSummary = canCapability('incidents', 'generate_executive_summary');
  const canViewEvidence = canCapability('incidents', 'view_evidence');
  const canViewAttackStory = canCapability('incidents', 'view_attack_story');
  const canViewAttackReplay = canCapability('incidents', 'view_attack_replay');
  const canViewExplainability = canCapability('incidents', 'view_explainability');
  const canSimulateResponse = canCapability('incidents', 'simulate_response');
  const canViewSlaPrediction = canCapability('incidents', 'view_sla_prediction');
  const canUseAutopilot = !readOnly && canCapability('incidents', 'use_autopilot');
  const canGenerateNextBestQueries = canCapability('next_best_query', 'generate');
  const canGenerateCustomerUpdate = canCapability('incidents', 'generate_customer_update');
  const canUseGuidedTriage = canCapability('incidents', 'use_guided_triage');
  const canViewVulnerabilityCorrelation = canCapability('incidents', 'view_vulnerability_correlation');
  const canFindSimilarIncidents = canCapability('incidents', 'find_similar_incidents');
  const canAnalyzeAttackStory = !readOnly && canCapability('incidents', 'analyze_attack_story');
  const canOpenExternalLink = canCapability('incidents', 'open_external_link');
  const canManageLabels = !readOnly && canCapability('incidents', 'manage_labels');
  const canBuildEvidenceQuery = canCapability('incidents', 'build_evidence_query');
  const canRunEvidenceQuery = !readOnly && canCapability('incidents', 'run_evidence_query');
  const canSeeCaseEvidence = canCapability('case_evidence', 'read');
  // Keep the Case Evidence launch action visible whenever an incident has a customer context.
  // The panel itself still respects Case Evidence capabilities and backend routes enforce them.
  // This avoids hiding the feature when the capability cache is stale or newly-added module rows are missing.
  const canOpenCaseEvidencePanel = !readOnly;
  const canSetAdvancedHuntingQuery = canCapability('advanced_hunting', 'set_query');
  const isClosed = effectiveStatus(incident) === 'closed';
  const statusSelectionValue = isClosed
    ? 'Closed'
    : effectiveStatus(incident) === 'active'
      ? 'Active'
      : effectiveStatus(incident) === 'new'
        ? 'New'
        : 'Redirected';
  const canSelectStatus = !!onStatusIncident && ((isClosed && canReopenIncident) || (!isClosed && canChangeIncidentStatus));
  const [comments, setComments] = useState<IncidentComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);
  const [postFallbackPrompt, setPostFallbackPrompt] = useState<string | null>(null);
  const [postFallbackAllowed, setPostFallbackAllowed] = useState(false);
  const [executiveSummary, setExecutiveSummary] = useState<string | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [labelsDraft, setLabelsDraft] = useState<string[]>(() => normaliseIncidentLabels(incident));
  const [labelInput, setLabelInput] = useState('');
  const [labelsSaving, setLabelsSaving] = useState(false);
  const [labelsError, setLabelsError] = useState<string | null>(null);
  const [labelsFallbackPrompt, setLabelsFallbackPrompt] = useState<string | null>(null);
  const [labelsFallbackAllowed, setLabelsFallbackAllowed] = useState(false);
  const [eventsEvidenceLoading, setEventsEvidenceLoading] = useState(false);
  const [eventsEvidenceError, setEventsEvidenceError] = useState<string | null>(null);

  // ── AI Investigation state ──
  const [aiAnalysis, setAiAnalysis] = useState<string | null>(null);
  const [aiVerdict, setAiVerdict] = useState<{
    verdict: 'TRUE POSITIVE' | 'FALSE POSITIVE' | 'BENIGN POSITIVE';
    confidence: 'Low' | 'Medium' | 'High';
    rationale: string;
  } | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiNoConfig, setAiNoConfig] = useState(false);

  const aiBlocks = useMemo(() => (aiAnalysis ? parseMarkdown(aiAnalysis) : []), [aiAnalysis]);

  // ── Live SSE streaming state ──
  // Activity log accumulates every event the agent emits (KQL queries, OSINT
  // lookups, tool results, phase markers). `streamPhase` drives the heading.
  // `streamCached` is true when the SSE returned a cached posted result, so
  // we skip the live feed UI and just hydrate the verdict/markdown directly.
  const [streamEvents, setStreamEvents] = useState<InvestigationStreamFrame[]>([]);
  const [streamPhase, setStreamPhase] = useState<'evidence_collecting' | 'agent_thinking' | 'posting' | 'done' | null>(null);
  const [streamActive, setStreamActive] = useState(false);
  const [streamError, setStreamError] = useState<string | null>(null);
  const [streamCached, setStreamCached] = useState(false);
  // AbortController for the in-flight stream so we can cancel on unmount or
  // when the user re-runs (force) — prevents two parallel SSE consumers.
  const streamAbortRef = useRef<AbortController | null>(null);
  // Have we already kicked off the auto-trigger for *this* incident? Guarded
  // so re-renders (or rapid open/close) don't spam the API.
  const autoTriggeredRef = useRef<string | null>(null);

  // ── Deep Investigate (SentinelIR-Agent) drawer state ──
  // Replaces the legacy "Cross-alert summary" surface. Manual-only — opens the
  // shared DeepInvestigationDrawer used by IncidentsTab; the drawer handles
  // its own polling, progress UI and structured-report rendering. Hidden from
  // customer_viewer role and when no customerId is in scope.
  const [deepDrawerOpen, setDeepDrawerOpen] = useState(false);

  // ── Case Evidence drawer/panel state ─────────────────────────────────────
  // Kept as a bottom incident action so SOC analysts can reach evidence upload
  // even when the Evidence summary card is collapsed or permission cache is stale.
  const [caseEvidenceOpen, setCaseEvidenceOpen] = useState(false);

  // ── Attack Story / Investigation Graph panel state ─────────────────────
  // Shows a Sentinel/Defender reconstructed incident graph and timeline with
  // an optional AI analysis pass grounded in the returned nodes/edges.
  const [attackStoryOpen, setAttackStoryOpen] = useState(false);

  // ── Incident explainability panel state ─────────────────────────────────
  const [explainabilityOpen, setExplainabilityOpen] = useState(false);

  // ── Response simulation / SLA decision intelligence state ────────────────
  const [whatIfOpen, setWhatIfOpen] = useState(false);
  const [slaPredictionOpen, setSlaPredictionOpen] = useState(false);
  const [autopilotOpen, setAutopilotOpen] = useState(false);
  const [nextBestQueryOpen, setNextBestQueryOpen] = useState(false);
  const [customerUpdateOpen, setCustomerUpdateOpen] = useState(false);
  const [guidedTriageOpen, setGuidedTriageOpen] = useState(false);
  const [vulnerabilityCorrelationOpen, setVulnerabilityCorrelationOpen] = useState(false);
  const [similarIncidentsOpen, setSimilarIncidentsOpen] = useState(false);

  // ── On-demand "Generate Report" PDF (Pattern A) ─────────────────────────
  // Calls POST /incidents/:id/report. The server runs Azure OpenAI with
  // the structured incident-report prompt and returns JSON; we render
  // it to a branded PDF entirely client-side using the same chrome as
  // the daily/weekly/monthly reports. Restricted to admin / soc_manager /
  // SOC L1 / SOC L2 — the customer-facing portal and other roles never
  // see this.
  const REPORT_OUTPUT_TYPE = 'Full Incident Report';
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const canGenerateReport =
    user?.role === 'admin' ||
    user?.role === 'developer' ||
    user?.role === 'soc_manager' ||
    user?.role === 'soc_l1' ||
    user?.role === 'soc_l2';

  // ── Live enrichment: pull full entity/MITRE/alert details on open ──
  const [enrichedEntities, setEnrichedEntities] = useState<Entity[] | null>(null);
  const [enrichedTactics, setEnrichedTactics] = useState<string[] | null>(null);
  const [enrichedAlertCount, setEnrichedAlertCount] = useState<number | null>(null);
  const [enrichedTimeline, setEnrichedTimeline] = useState<TimelineEntry[] | null>(null);
  const [enriching, setEnriching] = useState(false);

  // Related incidents (cross-incident entity pivot)
  const [relatedIncidents, setRelatedIncidents] = useState<RelatedIncidentRow[] | null>(null);
  const [relatedLoading, setRelatedLoading] = useState(false);
  // Collapsed by default so the modal isn't dominated by a list analysts often don't need.
  const [relatedIncidentsOpen, setRelatedIncidentsOpen] = useState(false);

  // Automatic IOC reputation lookup against threat-intel providers (VT/AbuseIPDB/OTX/Spur).
  // Keyed by lowercased IOC value so the lookup is case-insensitive.
  const [reputationMap, setReputationMap] = useState<Map<string, ReputationReport>>(new Map());
  const [reputationLoading, setReputationLoading] = useState(false);
  const [reputationProvidersConfigured, setReputationProvidersConfigured] = useState<number | null>(null);
  const [reputationError, setReputationError] = useState<string | null>(null);

  const displayedEntities: Entity[] = enrichedEntities && enrichedEntities.length > 0
    ? enrichedEntities
    : (incident.entities ?? []);
  const displayedTactics: string[] = enrichedTactics && enrichedTactics.length > 0
    ? enrichedTactics
    : (incident.mitreTactics ?? []);
  const displayedAlertCount: number = enrichedAlertCount && enrichedAlertCount > 0
    ? enrichedAlertCount
    : (incident.alertsCount ?? 0);
  const displayedTimeline: TimelineEntry[] = enrichedTimeline ?? [];

  // ── Primary entity detection (UPN / hostname) for sub-panels ──
  const primaryUpn: string | null = useMemo(() => {
    for (const e of displayedEntities) {
      const t = (getEntityType(e) ?? '').toLowerCase();
      const name = getEntityName(e);
      if (t === 'account' && name.includes('@')) return name;
    }
    for (const e of displayedEntities) {
      const name = getEntityName(e);
      if (name.includes('@') && !name.includes(' ')) return name;
    }
    return null;
  }, [displayedEntities]);

  const primaryHost: string | null = useMemo(() => {
    for (const e of displayedEntities) {
      const t = (getEntityType(e) ?? '').toLowerCase();
      const name = getEntityName(e);
      if ((t === 'host' || t === 'machine') && name && !name.includes('@')) return name;
    }
    return null;
  }, [displayedEntities]);

  // Compact text summary used to seed the agent's system context.
  const incidentSummary: string = useMemo(() => {
    const parts: string[] = [];
    parts.push(`Incident #${sentinelIncidentNumber(incident) || incident.id}: ${incident.title}`);
    parts.push(`Severity: ${incident.severity} · Status: ${incident.status}` + (incident.assignedTo ? ` · Assigned: ${incident.assignedTo}` : ''));
    if (displayedTactics.length > 0) parts.push(`MITRE Tactics: ${displayedTactics.join(', ')}`);
    if (displayedEntities.length > 0) {
      const entitySummary = displayedEntities.slice(0, 12).map(e => {
        const t = getEntityType(e);
        return t ? `${t}=${getEntityName(e)}` : getEntityName(e);
      }).join(', ');
      parts.push(`Entities: ${entitySummary}`);
    }
    if (displayedAlertCount) parts.push(`Alerts in incident: ${displayedAlertCount}`);
    if (incident.description) parts.push(`Description: ${String(incident.description).slice(0, 600)}`);
    return parts.join('\n');
  }, [incident, displayedTactics, displayedEntities, displayedAlertCount]);

  useEffect(() => {
    let cancelled = false;
    // Clear any prior incident's enrichment so a slow/failed fetch can't leak stale data.
    setEnrichedEntities(null);
    setEnrichedTactics(null);
    setEnrichedAlertCount(null);
    setEnrichedTimeline(null);
    setRelatedIncidents(null);
    setRelatedLoading(false);
    setExecutiveSummary(null);
    setSummaryError(null);
    setLabelsDraft(normaliseIncidentLabels(incident));
    setLabelInput('');
    setLabelsError(null);
    setLabelsFallbackPrompt(null);
    // Reset auto-reputation so a slow lookup from a previous incident can't leak in.
    // Also reset the provider count so the IOC effect won't fire against stale entities
    // until the new incident's provider check resolves.
    setReputationMap(new Map());
    setReputationLoading(false);
    setReputationError(null);
    setReputationProvidersConfigured(null);
    // Close the deep-investigate drawer if the user navigated to a different
    // incident — the drawer is bound to a specific incident id and would
    // otherwise show stale progress for the previous one.
    setDeepDrawerOpen(false);
    setCaseEvidenceOpen(false);
    setAttackStoryOpen(false);
    setExplainabilityOpen(false);
    setWhatIfOpen(false);
    setSlaPredictionOpen(false);
    setAutopilotOpen(false);
    setNextBestQueryOpen(false);
    const params = new URLSearchParams();
    if (customerId) params.set('customerId', customerId);
    if (incident.incidentNumber) params.set('incidentNumber', incident.incidentNumber);
    if (incident.graphIncidentId) params.set('graphIncidentId', incident.graphIncidentId);
    setEnriching(true);
    apiFetch<{ entities: Entity[]; mitreTactics: string[]; alertsCount: number; timeline?: TimelineEntry[]; source: string }>(
      `/incidents/${encodeURIComponent(incident.id)}/enrich?${params.toString()}`
    )
      .then((data) => {
        if (cancelled) return;
        setEnrichedEntities(Array.isArray(data.entities) ? data.entities : []);
        setEnrichedTactics(Array.isArray(data.mitreTactics) ? data.mitreTactics : []);
        setEnrichedAlertCount(typeof data.alertsCount === 'number' ? data.alertsCount : 0);
        setEnrichedTimeline(Array.isArray(data.timeline) ? data.timeline : []);
      })
      .catch(() => { /* silent — leaves cleared state so we fall back to the incident's original fields */ })
      .finally(() => { if (!cancelled) setEnriching(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incident.id]);

  // After entities are known, look up other recent incidents that share any of them.
  // Only runs once enrichment has resolved (enrichedEntities !== null) to avoid
  // an extra pre-enrichment fetch + a second post-enrichment fetch.
  useEffect(() => {
    if (!incident.incidentNumber) return;
    if (enrichedEntities === null) return; // wait until enrichment resolves
    const entityValues = Array.from(
      new Set(
        enrichedEntities
          .map((e) => getEntityName(e))
          .filter(isStrongEntityValue)
      )
    );
    if (entityValues.length === 0) {
      setRelatedIncidents([]);
      setRelatedLoading(false);
      return;
    }
    let cancelled = false;
    setRelatedLoading(true);
    const params = new URLSearchParams();
    if (customerId) params.set('customerId', customerId);
    params.set('incidentNumber', incident.incidentNumber);
    apiFetch<{ related: RelatedIncidentRow[] }>(
      `/incidents/${encodeURIComponent(incident.id)}/related?${params.toString()}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entityValues, days: 30 }),
      }
    )
      .then((data) => {
        if (cancelled) return;
        setRelatedIncidents(Array.isArray(data.related) ? data.related : []);
      })
      .catch(() => { if (!cancelled) setRelatedIncidents([]); })
      .finally(() => { if (!cancelled) setRelatedLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incident.id, enrichedEntities]);

  // Cache the provider-configured count so we don't fire the batch endpoint when zero
  // providers have keys (which would just return per-IOC `no_provider_configured` errors).
  // Re-fetched on incident change so saving a new key in Settings is picked up next time.
  useEffect(() => {
    let cancelled = false;
    apiFetch<{ providers: Record<string, { configured: boolean }> }>(`/ioc/reputation/providers`)
      .then((data) => {
        if (cancelled) return;
        const count = Object.values(data.providers ?? {}).filter(p => p.configured).length;
        setReputationProvidersConfigured(count);
      })
      .catch(() => { if (!cancelled) setReputationProvidersConfigured(0); });
    return () => { cancelled = true; };
  }, [incident.id]);

  // Auto-enrich any IP / domain / URL / hash entities against the configured TI providers.
  // Runs once enrichment resolves AND we know at least one provider is configured.
  useEffect(() => {
    if (enrichedEntities === null) return; // wait for Sentinel enrichment
    if (reputationProvidersConfigured === null) return; // wait for provider count
    if (reputationProvidersConfigured === 0) return; // skip the call entirely if no keys
    // Build a unique IOC list (cap at 25 — the batch endpoint's limit).
    const seen = new Set<string>();
    const iocs: string[] = [];
    for (const e of displayedEntities) {
      const picked = pickIocFromEntity(e);
      if (!picked) continue;
      const key = picked.value.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      iocs.push(picked.value);
      if (iocs.length >= 25) break;
    }
    if (iocs.length === 0) return;
    // /ioc/reputation/batch is now gated by `requireTabRead('threat_intel')` on
    // the server, which means it requires a resolvable customerId. Skip the
    // auto-enrichment in the default workspace rather than triggering a 400.
    if (!customerId) return;
    let cancelled = false;
    setReputationLoading(true);
    setReputationError(null);
    apiFetch<{ reports: BatchReportEntry[] }>(`/ioc/reputation/batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ iocs, customerId }),
    })
      .then((data) => {
        if (cancelled) return;
        const map = new Map<string, ReputationReport>();
        for (const entry of data.reports ?? []) {
          if (entry.ok && entry.report) {
            map.set(entry.report.ioc.toLowerCase(), entry.report);
          }
        }
        setReputationMap(map);
      })
      .catch((err) => {
        if (!cancelled) setReputationError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => { if (!cancelled) setReputationLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incident.id, enrichedEntities, reputationProvidersConfigured]);

  // Stream a SOC L1 auto-investigation via Server-Sent Events. Drives the
  // live activity feed (KQL queries, OSINT lookups, agent thinking) and, on
  // completion, hydrates the verdict banner + Markdown RCA. Cancels any
  // in-flight stream first so re-runs don't double up.
  //
  // We use fetch + ReadableStream (not EventSource) because:
  //   1. EventSource is GET-only — we need to POST `{customerId, force}`
  //   2. EventSource ignores the cookie-based auth on cross-origin proxied
  //      requests in some browsers; fetch with credentials behaves correctly.
  async function runAiInvestigation(opts: { force: boolean; auto?: boolean } = { force: true }) {
    // Cancel any in-flight stream to avoid duplicate consumers.
    streamAbortRef.current?.abort();
    const ac = new AbortController();
    streamAbortRef.current = ac;

    setAiLoading(true);
    setAiError(null);
    setAiNoConfig(false);
    if (opts.force) {
      // A forced re-run wipes stale results so the UI clearly reflects "rerun
      // in progress". Auto-triggers on open keep any prior cached verdict.
      setAiAnalysis(null);
      setAiVerdict(null);
    }
    setStreamEvents([]);
    setStreamError(null);
    setStreamCached(false);
    setStreamPhase(null);
    setStreamActive(true);
    // Local accumulator that the finally-block safety net reads to detect
    // an empty stream. We can't use the `streamEvents` state here — the
    // finally block captures the stale post-reset closure value, not the
    // latest state — so we mirror frames into this array as they arrive.
    const framesReceived: InvestigationStreamFrame[] = [];

    // SSE endpoint URL. Use the canonical `/api` root-relative base — the
    // Replit proxy routes `/api` to the api-server artifact in both dev and
    // prod (see apiClient.ts). Resolving via Vite's BASE_URL would prepend
    // the dashboard's path prefix and break in non-root deployments.
    const url = `/api/incidents/${encodeURIComponent(incident.id)}/auto-investigate/stream`;

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify({ customerId, force: opts.force, armIncidentName: incident.armIncidentName, graphIncidentId: incident.graphIncidentId }),
        credentials: 'include',
        signal: ac.signal,
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}: ${txt.slice(0, 300) || res.statusText}`);
      }
      if (!res.body) throw new Error('Response has no body - streaming not supported by this transport.');

      // SSE parser: accumulate text, split on the blank-line frame separator
      // (\n\n), then extract `data:` lines from each frame and JSON-parse.
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let sepIdx: number;
        while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, sepIdx);
          buffer = buffer.slice(sepIdx + 2);
          // A frame may have multiple `data:` lines per spec; concat them.
          const dataLines = frame.split('\n').filter(l => l.startsWith('data:')).map(l => l.slice(5).trimStart());
          if (dataLines.length === 0) continue;
          let parsed: InvestigationStreamFrame;
          try {
            parsed = JSON.parse(dataLines.join('\n')) as InvestigationStreamFrame;
          } catch {
            continue;
          }
          // Append every frame to the activity log (the renderer filters out
          // ping/open). Then react to specific frames that drive UI state.
          // Also track in a local accumulator so the finally-block safety
          // net can read the latest count without stale closure capture.
          setStreamEvents(prev => [...prev, parsed]);
          framesReceived.push(parsed);
          if (parsed.kind === 'phase') {
            setStreamPhase(parsed.phase);
          } else if (parsed.kind === 'cached') {
            setStreamCached(true);
            setAiAnalysis(parsed.summary);
            if (parsed.verdict) setAiVerdict(parsed.verdict);
            setStreamPhase('done');
          } else if (parsed.kind === 'result') {
            if (parsed.summary) setAiAnalysis(parsed.summary);
            if (parsed.verdict) setAiVerdict(parsed.verdict);
            // Surface BOTH `failed` and `skipped` errors via streamError so
            // the live panel shows a clear inline explanation instead of
            // just an "Investigation skipped" row with no context. Without
            // this, a closed/redirected incident would render a single
            // amber line and leave the user wondering what happened.
            if ((parsed.status === 'failed' || parsed.status === 'skipped') && parsed.error) {
              const msg = parsed.error.toLowerCase();
              if (msg.includes('not configured') || msg.includes('foundry')) setAiNoConfig(true);
              setStreamError(parsed.error);
              if (parsed.status === 'failed') setAiError(parsed.error);
            }
            // On successful posting, clear any stale aiError/streamError
            // left over from a prior `failed` frame (we emit one before
            // retrying a failed cached row). Without this, the panel would
            // show a successful verdict alongside a residual red error
            // banner from the previous attempt.
            if (parsed.status === 'posted') {
              setAiError(null);
              setStreamError(null);
            }
          } else if (parsed.kind === 'error') {
            const m = parsed.message.toLowerCase();
            if (m.includes('not configured') || m.includes('foundry')) setAiNoConfig(true);
            setStreamError(parsed.message);
            setAiError(parsed.message);
          }
        }
      }
    } catch (e) {
      // AbortError from explicit cancel/unmount — don't surface it as a real
      // error (the user moved on or restarted intentionally).
      if (e instanceof Error && e.name === 'AbortError') return;
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.toLowerCase().includes('not configured') || msg.toLowerCase().includes('foundry')) setAiNoConfig(true);
      setAiError(msg);
      setStreamError(msg);
    } finally {
      // Only clear loading state if this is still the active stream (the
      // user might have already kicked off another).
      if (streamAbortRef.current === ac) {
        setAiLoading(false);
        setStreamActive(false);
        // Defense in depth: if the stream ends without ANY meaningful
        // event (no cached, no result, no error, no phase, no agent/
        // evidence frames), surface a clear inline error rather than
        // letting the panel vanish silently. This guards against any
        // future backend path that forgets to emit a terminal frame.
        // We only overwrite streamError if it's still null — a more
        // specific transport/backend error must take precedence.
        const meaningful = framesReceived.some(f => f.kind !== 'open' && f.kind !== 'ping');
        if (!meaningful) {
          setStreamError(curr =>
            curr ??
            'The auto-investigator finished without sending any events. This is unexpected - check the API server logs for errors. Click "Re-analyze with AI" to retry.',
          );
        }
      }
    }
  }

  // Auto-trigger on modal open: kick off the streaming investigation if the
  // incident hasn't already been posted. If a posted result exists in the
  // dashboard cache we skip — the user can still hit "Re-analyze" to force
  // a fresh run. The ref guards against double-fires from React strict-mode
  // double-mounts and parent re-renders.
  useEffect(() => {
    if (readOnly) return;
    if (autoTriggeredRef.current === incident.id) return;
    // Lifecycle gate — auto-investigation only runs on `new` / `active`
    // (i.e. "New" or "In Progress") incidents. This MUST stay in sync
    // with the backend allow-list in `lib/incidentLifecycle.ts`. We also
    // gate client-side here (in addition to the server's gate) so that
    // closed/redirected incidents skip the network round-trip entirely
    // — the live panel never even renders, the user can still hit the
    // manual "Investigate with AI" button to force a fresh run if they
    // really want one.
    const lifecycleStatus = (incident.status ?? '').toLowerCase();
    if (lifecycleStatus !== 'new' && lifecycleStatus !== 'active') return;
    const ai = incident.autoInvestigation;
    if (ai && ai.status === 'posted') return;
    autoTriggeredRef.current = incident.id;
    // Fire-and-forget; the streaming function manages its own state.
    void runAiInvestigation({ force: false, auto: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incident.id, incident.status, readOnly]);

  // Cancel the SSE consumer if the modal closes mid-stream.
  useEffect(() => {
    return () => {
      streamAbortRef.current?.abort();
    };
  }, []);


  async function generateExecutiveSummary() {
    if (!canGenerateExecutiveSummary || summaryLoading) return;
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const res = await apiFetch<{ ok: boolean; summary: string }>(`/incidents/${encodeURIComponent(incident.id)}/executive-summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ incident, entities: displayedEntities, timeline: displayedTimeline }),
      });
      setExecutiveSummary(res.summary || 'No summary returned.');
    } catch (e) {
      setSummaryError(e instanceof Error ? e.message : String(e));
    } finally {
      setSummaryLoading(false);
    }
  }

  function addLabelDraft() {
    const label = labelInput.replace(/[<>\r\n\t]/g, ' ').replace(/\s{2,}/g, ' ').trim();
    if (!label) return;
    setLabelsDraft(prev => Array.from(new Set([...prev, label])).slice(0, 30));
    setLabelInput('');
  }

  async function saveLabels(useServicePrincipalFallback = false) {
    if (!canManageLabels || labelsSaving) return;
    setLabelsSaving(true);
    setLabelsError(null);
    if (!useServicePrincipalFallback) { setLabelsFallbackPrompt(null); setLabelsFallbackAllowed(false); }
    try {
      const qs = customerId ? `?customerId=${encodeURIComponent(customerId)}` : '';
      const res = await apiFetch<{ ok: boolean; labels: string[] }>(`/incidents/${encodeURIComponent(incident.id)}/labels${qs}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          labels: labelsDraft,
          armIncidentName: incident.armIncidentName,
          graphIncidentId: incident.graphIncidentId,
          useServicePrincipalFallback,
        }),
      });
      setLabelsDraft(Array.isArray(res.labels) ? res.labels : labelsDraft);
      setLabelsFallbackPrompt(null);
      setLabelsFallbackAllowed(false);
      notifyIncidentExecutionUpdated(incident.id);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.body?.['error'] === 'delegated_rbac_required') {
        setLabelsFallbackPrompt(typeof e.body['message'] === 'string' ? e.body['message'] as string : 'Your user identity could not update labels. Use Service Principal RBAC?');
        setLabelsFallbackAllowed(e.body?.['canUseServicePrincipalFallback'] === true);
      } else {
        setLabelsError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setLabelsSaving(false);
    }
  }

  const defenderIncidentId = incident.graphIncidentId || incident.incidentNumber;
  const openMicrosoftIncidentUrl = incidentProviderLabel(incident).toLowerCase().includes('defender')
    ? `https://security.microsoft.com/incidents/${defenderIncidentId}`
    : (incident.webUrl || `https://security.microsoft.com/incidents/${defenderIncidentId}`);

  function getIncidentEventsId(): string {
    return String(incident.armIncidentName || incident.id || incident.incidentNumber || '').trim();
  }

  function handleRunKql(query: string) {
    if (!canSetAdvancedHuntingQuery) {
      navigator.clipboard.writeText(query).catch(() => null);
      setEventsEvidenceError('Your role is not allowed to set Advanced Hunting queries from pivots. The query was copied to clipboard instead.');
      return;
    }
    if (onRunKql) {
      onRunKql(query);
      onClose();
    } else {
      navigator.clipboard.writeText(query).catch(() => null);
      setEventsEvidenceError('Advanced Hunting is not available in this view. The query was copied to clipboard.');
    }
  }

  async function openEventsEvidenceQuery() {
    if (!canBuildEvidenceQuery) {
      setEventsEvidenceError('Your role is not allowed to build Events Evidence queries. Ask an administrator to enable Incidents / Build Events Evidence Query.');
      return;
    }
    const incidentId = getIncidentEventsId();
    if (!incidentId) {
      setEventsEvidenceError('Could not determine the Sentinel incident id/name required to resolve linked alerts.');
      return;
    }

    setEventsEvidenceLoading(true);
    setEventsEvidenceError(null);
    try {
      const result = await fetchIncidentEventsRuleQuery({
        incidentId,
        customerId,
        armIncidentName: incident.armIncidentName,
        incidentNumber: incident.incidentNumber,
        limit: 200,
      });
      if (!result.query?.trim()) {
        setEventsEvidenceError('The backend did not return an Events Evidence query for this incident.');
        return;
      }
      handleRunKql(result.query);
    } catch (e) {
      setEventsEvidenceError(e instanceof Error ? e.message : String(e));
    } finally {
      setEventsEvidenceLoading(false);
    }
  }

  function buildCommentsQs() {
    const p = new URLSearchParams();
    if (customerId) p.set('customerId', customerId);
    if (incident.armIncidentName) p.set('armIncidentName', incident.armIncidentName);
    if (incident.graphIncidentId) p.set('graphIncidentId', incident.graphIncidentId);
    return p.toString();
  }

  useEffect(() => {
    let cancelled = false;
    setCommentsLoading(true);
    const qs = buildCommentsQs();
    apiFetch<IncidentComment[]>(`/incidents/${encodeURIComponent(incident.id)}/comments${qs ? '?' + qs : ''}`)
      .then(list => { if (!cancelled) setComments(Array.isArray(list) ? list : []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setCommentsLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incident.id]);

  async function postComment(useServicePrincipalFallback = false) {
    const trimmed = newComment.trim();
    if (!trimmed || !canComment) return;
    setPosting(true);
    setPostError(null);
    if (!useServicePrincipalFallback) { setPostFallbackPrompt(null); setPostFallbackAllowed(false); }

    try {
      const qs = customerId ? `?customerId=${encodeURIComponent(customerId)}` : '';
      const res = await apiFetch<{ ok: boolean }>(`/incidents/${encodeURIComponent(incident.id)}/comments${qs}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: trimmed,
          armIncidentName: incident.armIncidentName,
          graphIncidentId: incident.graphIncidentId,
          useServicePrincipalFallback,
        }),
      });
      if (!res.ok) throw new Error('Failed to sync comment');
      setComments(prev => [...prev, {
        id: `local-${Date.now()}`,
        message: trimmed,
        author: 'You',
        createdTime: new Date().toISOString(),
      }]);
      setNewComment('');
      setPostFallbackPrompt(null);
      setPostFallbackAllowed(false);
      notifyIncidentExecutionUpdated(incident.id);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.body?.["error"] === "delegated_rbac_required") {
        const msg = typeof e.body["message"] === 'string'
          ? e.body["message"] as string
          : 'Your user identity could not complete this delegated Sentinel action. Do you still wish to proceed using the Service Principal RBAC?';
        setPostFallbackPrompt(msg);
        setPostFallbackAllowed(e.body?.["canUseServicePrincipalFallback"] === true);
        setPostError(null);
      } else {
        setPostError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setPosting(false);
    }
  }

  const wrapperClass = fullPage
    ? "w-full"
    : "fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto py-8 px-4";
  const innerClass = fullPage
    ? "bg-card rounded-xl shadow-xl border border-border w-full"
    : "bg-card rounded-xl shadow-2xl w-full max-w-2xl";
  const wrapperOnClick = fullPage ? undefined : onClose;
  const innerOnClick = fullPage ? undefined : (e: React.MouseEvent) => e.stopPropagation();

  return (
    <div className={wrapperClass} onClick={wrapperOnClick}>
      <div className={innerClass} onClick={innerOnClick}>
        <div className="p-6">
          <div className="flex justify-between items-start mb-4 gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-mono text-muted-foreground mb-1">Incident #{sentinelIncidentNumber(incident)}</p>
              <h2 className="text-lg font-bold text-foreground leading-snug break-words">{incident.title}</h2>
              {incident.autoInvestigation?.verdict && (
                <span className={`mt-1.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold border ${
                  incident.autoInvestigation.verdict.verdict === 'TRUE POSITIVE'
                    ? 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/40'
                    : incident.autoInvestigation.verdict.verdict === 'FALSE POSITIVE' || incident.autoInvestigation.verdict.verdict === 'BENIGN POSITIVE'
                      ? 'bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/40'
                      : 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40'
                }`}>
                  {incident.autoInvestigation.verdict.verdict}
                  <span className="font-mono opacity-80">· Confidence: {incident.autoInvestigation.verdict.confidence}</span>
                </span>
              )}
            </div>
            {fullPage ? (
              <button
                onClick={onClose}
                className="shrink-0 text-xs font-semibold border border-border bg-muted/50 text-foreground hover:bg-muted px-3 py-1.5 rounded-lg transition-colors"
                title="Back to dashboard"
              >
                ← Back
              </button>
            ) : (
              <button onClick={onClose} className="text-3xl text-muted-foreground hover:text-foreground leading-none">&times;</button>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 mb-4">
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">Severity</p>
              {onSeverityIncident && canChangeIncidentSeverity ? (
                <select
                  value={incident.severity}
                  onChange={(event) => {
                    const severity = event.currentTarget.value as Incident['severity'];
                    if (severity !== incident.severity && severity !== 'Critical') onSeverityIncident(severity);
                  }}
                  aria-label="Change incident severity"
                  className={`rounded border border-transparent px-2 py-1 text-xs font-semibold focus:border-primary focus:outline-none ${
                    incident.severity === 'High' || incident.severity === 'Critical' ? 'severity-high' : incident.severity === 'Medium' ? 'severity-medium' : incident.severity === 'Low' ? 'severity-low' : 'severity-informational'
                  }`}
                >
                  {incident.severity === 'Critical' && <option value="Critical" disabled>Critical</option>}
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                  <option value="Informational">Informational</option>
                </select>
              ) : (
                <span className={`text-xs font-semibold px-2 py-1 rounded ${incident.severity === 'High' || incident.severity === 'Critical' ? 'severity-high' : incident.severity === 'Medium' ? 'severity-medium' : incident.severity === 'Low' ? 'severity-low' : 'severity-informational'}`}>
                  {incident.severity}
                </span>
              )}
            </div>
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">Status</p>
              {canSelectStatus ? (
                <select
                  value={statusSelectionValue}
                  onChange={(event) => {
                    const status = event.currentTarget.value as IncidentStatusSelection;
                    if (status === 'New' || status === 'Active') onStatusIncident?.(status);
                  }}
                  aria-label="Change incident status"
                  className={`rounded border border-transparent px-2 py-1 text-xs font-semibold focus:border-primary focus:outline-none ${
                    isClosed ? 'status-closed' : effectiveStatus(incident) === 'active' ? 'status-active' : 'status-new'
                  }`}
                >
                  {isClosed && <option value="Closed" disabled>Resolved</option>}
                  {statusSelectionValue === 'Redirected' && <option value="Redirected" disabled>Redirected</option>}
                  <option value="New">New</option>
                  <option value="Active">In Progress</option>
                </select>
              ) : (
                <span className={`text-xs font-semibold px-2 py-1 rounded ${isClosed ? 'status-closed' : effectiveStatus(incident) === 'active' ? 'status-active' : 'status-new'}`}>
                  {isClosed ? 'Resolved' : effectiveStatus(incident) === 'active' ? 'In Progress' : 'New'}
                </span>
              )}
            </div>
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="text-xs text-muted-foreground">Assigned To</p>
                {onAssignIncident && (
                  <button
                    type="button"
                    onClick={onAssignIncident}
                    className="text-[11px] font-semibold text-primary hover:underline"
                    title="Assign this incident to a SOC analyst"
                  >
                    {incident.assignedTo ? 'Change' : 'Assign'}
                  </button>
                )}
              </div>
              <p className="text-sm text-foreground">{incident.assignedTo || <em className="text-muted-foreground">Unassigned</em>}</p>
            </div>
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">Alerts</p>
              <p className="text-sm text-foreground font-semibold">
                {displayedAlertCount} alert(s)
                {enriching && displayedAlertCount === 0 && <span className="ml-1 text-xs font-normal italic text-muted-foreground">loading…</span>}
              </p>
            </div>
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">Created</p>
              <p className="text-sm text-foreground">{formatDate(incident.createdTime)}</p>
            </div>
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">Last Updated</p>
              <p className="text-sm text-foreground">{formatDate(incident.lastUpdateTime)}</p>
            </div>
          </div>

          <div className="grid gap-3 mb-4">
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Alert / Incident Provider</p>
                  {(() => {
                    const provider = incidentProviderLabel(incident);
                    return <span className={`text-xs font-semibold px-2 py-1 rounded-full border ${providerBadgeClass(provider)}`}>{provider}</span>;
                  })()}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {canOpenExternalLink && incident.webUrl && (
                    <a
                      href={incident.webUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-blue-600 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40 transition-colors"
                      title="Open this specific incident in the Microsoft Sentinel portal"
                    >
                      Open in Microsoft Sentinel
                    </a>
                  )}
                  {canOpenExternalLink && openMicrosoftIncidentUrl && (
                    <a
                      href={openMicrosoftIncidentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                      title="Open this incident in Microsoft Defender XDR / Sentinel"
                    >
                      Open in Microsoft Defender XDR
                    </a>
                  )}
                </div>
              </div>
            </div>

            {!readOnly && customerId ? (
              <NativeResponseActions
                customerId={customerId}
                entityType="incident"
                entityValue={String(incident.armIncidentName || incident.id || incident.incidentNumber || '')}
                source="incidentops"
              />
            ) : null}

            {canGenerateExecutiveSummary && (
              <div className="bg-muted/50 rounded-lg p-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <p className="text-xs text-muted-foreground font-semibold">Executive Summary</p>
                  <button
                    type="button"
                    onClick={generateExecutiveSummary}
                    disabled={summaryLoading}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
                  >
                    {summaryLoading ? 'Generating…' : executiveSummary ? 'Regenerate' : 'Generate with AI'}
                  </button>
                </div>
                {summaryError && <p className="text-xs text-red-600 dark:text-red-400 mb-2">{summaryError}</p>}
                <p className="text-sm text-foreground leading-relaxed">
                  {executiveSummary || 'Generate a one-paragraph executive summary for leadership review.'}
                </p>
              </div>
            )}

            {canViewEvidence && (
              <div className="bg-muted/50 rounded-lg p-3 min-w-0">
                <p className="text-xs text-muted-foreground font-semibold mb-2">Evidence</p>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => void openEventsEvidenceQuery()}
                    disabled={eventsEvidenceLoading || !canBuildEvidenceQuery}
                    className="rounded-lg border border-border bg-card hover:bg-muted/70 p-3 text-left transition disabled:cursor-not-allowed disabled:opacity-60"
                    title="Resolve linked Sentinel alerts/rules and open the generated Events Evidence query in Advanced Hunting"
                  >
                    <p className="text-lg font-bold text-foreground">{eventsEvidenceLoading ? '…' : displayedEntities.length}</p>
                    <p className="text-xs text-blue-600 dark:text-blue-300">{eventsEvidenceLoading ? 'Building…' : 'Events'}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => onRunKql ? handleRunKql(buildAlertsQuery(incident.incidentNumber)) : undefined}
                    className="rounded-lg border border-border bg-card hover:bg-muted/70 p-3 text-left transition"
                    title="Open correlated alerts in Advanced Hunting"
                  >
                    <p className="text-lg font-bold text-foreground">{displayedAlertCount}</p>
                    <p className="text-xs text-blue-600 dark:text-blue-300">Alerts</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => onRunKql ? handleRunKql(buildIncidentEntitiesQuery(incident.incidentNumber)) : undefined}
                    className="rounded-lg border border-border bg-card hover:bg-muted/70 p-3 text-left transition"
                    title="Open incident entities in Advanced Hunting"
                  >
                    <p className="text-lg font-bold text-foreground">{displayedEntities.length}</p>
                    <p className="text-xs text-blue-600 dark:text-blue-300">Entities</p>
                  </button>
                </div>
                {eventsEvidenceError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{eventsEvidenceError}</p>}
                <div className="mt-3">
                  <IncidentEventsRuleQueryPanel
                    incidentId={getIncidentEventsId()}
                    customerId={customerId}
                    armIncidentName={incident.armIncidentName}
                    incidentNumber={incident.incidentNumber}
                    onOpenQuery={handleRunKql}
                    canBuildQuery={canBuildEvidenceQuery}
                    canSetQuery={canSetAdvancedHuntingQuery}
                    canRunQuery={canRunEvidenceQuery}
                  />
                </div>
              </div>
            )}

            {(labelsDraft.length > 0 || canManageLabels || mlScore) && (
              <div className="bg-muted/50 rounded-lg p-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <p className="text-xs text-muted-foreground font-semibold">Incident Labels / Tags</p>
                  {canManageLabels && (
                    <button
                      type="button"
                      onClick={() => saveLabels(false)}
                      disabled={labelsSaving}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
                    >
                      {labelsSaving ? 'Saving…' : 'Save labels'}
                    </button>
                  )}
                </div>
                {labelsError && <p className="text-xs text-red-600 dark:text-red-400 mb-2">{labelsError}</p>}
                {labelsFallbackPrompt && (
                  <div className="mb-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-2 text-xs text-amber-800 dark:text-amber-200">
                    <p className="font-semibold mb-1">Delegated user action failed</p>
                    <p className="mb-2">{labelsFallbackPrompt}</p>
                    <div className="flex gap-2">
                      {labelsFallbackAllowed && (
                        <button type="button" onClick={() => saveLabels(true)} className="px-2 py-1 rounded bg-amber-600 text-white font-semibold">Yes, use Service Principal</button>
                      )}
                      <button type="button" onClick={() => { setLabelsFallbackPrompt(null); setLabelsFallbackAllowed(false); }} className="px-2 py-1 rounded border border-amber-400">No, cancel</button>
                    </div>
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {mlScore && (() => {
                    const scoreClass = mlScore.mlPriorityScore >= 85
                      ? 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/40'
                      : mlScore.mlPriorityScore >= 70
                        ? 'bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/40'
                        : mlScore.mlPriorityScore >= 45
                          ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40'
                          : 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40';
                    return (
                      <span
                        className={`inline-flex items-center gap-1 text-xs font-black rounded px-2 py-1 border ${scoreClass}`}
                        title={mlScore.recommendedAction || 'ML priority score'}
                      >
                        ML Priority: {mlScore.mlPriorityScore}/100
                      </span>
                    );
                  })()}
                  {labelsDraft.length === 0 && !mlScore && <span className="text-xs italic text-muted-foreground">No labels.</span>}
                  {labelsDraft.map(label => (
                    <span key={label} className="inline-flex items-center gap-1 text-xs bg-muted border border-border rounded px-2 py-1">
                      {label}
                      {canManageLabels && (
                        <button type="button" onClick={() => setLabelsDraft(prev => prev.filter(x => x !== label))} className="text-muted-foreground hover:text-red-600" title={`Remove ${label}`}>×</button>
                      )}
                    </span>
                  ))}
                </div>
                {canManageLabels && (
                  <div className="flex gap-2">
                    <input
                      value={labelInput}
                      onChange={e => setLabelInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLabelDraft(); } }}
                      placeholder="Add label/tag…"
                      className="flex-1 text-xs border border-border rounded px-2 py-1 bg-background text-foreground"
                    />
                    <button type="button" onClick={addLabelDraft} className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-border hover:bg-muted">Add</button>
                  </div>
                )}
              </div>
            )}
          </div>

          {(displayedTactics.length > 0 || (enriching && (incident.mitreTactics ?? []).length === 0)) && (
            <div className="bg-muted/50 rounded-lg p-3 mb-4">
              <p className="text-xs text-muted-foreground mb-2">MITRE ATT&amp;CK Tactics</p>
              {displayedTactics.length === 0 && enriching ? (
                <p className="text-xs italic text-muted-foreground">loading…</p>
              ) : (
                <div className="flex max-h-[260px] min-w-0 flex-wrap content-start gap-1.5 overflow-y-auto pr-1">
                  {displayedTactics.map((t, i) => (
                    <span key={`${i}-${t}`} className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full font-medium">{t}</span>
                  ))}
                </div>
              )}
            </div>
          )}

          {primaryUpn && (
            <UserInfoPanel upn={primaryUpn} customerId={customerId} onRunKql={handleRunKql} />
          )}

          {(displayedTimeline.length > 0 || enriching) && (
            <div className="bg-muted/50 rounded-lg p-3 mb-4">
              <p className="text-xs text-muted-foreground mb-2 font-semibold">
                Alert Timeline
                {displayedTimeline.length > 0 && (
                  <span className="font-normal text-muted-foreground/70"> · {displayedTimeline.length} {displayedTimeline.length === 1 ? 'alert' : 'alerts'}</span>
                )}
              </p>
              {displayedTimeline.length === 0 && enriching ? (
                <p className="text-xs italic text-muted-foreground">loading alert timeline…</p>
              ) : (
                <ol className="relative border-l-2 border-border ml-2 space-y-3">
                  {displayedTimeline.map((a, i) => (
                    <li key={`${i}-${a.id || a.title}`} className="ml-4">
                      <span className="absolute -left-[7px] mt-1.5 h-3 w-3 rounded-full bg-primary border-2 border-card" aria-hidden="true" />
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${severityChipClass(a.severity)}`}>{a.severity || 'medium'}</span>
                            {a.alertWebUrl ? (
                              <a href={a.alertWebUrl} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-foreground hover:text-primary hover:underline truncate">{a.title}</a>
                            ) : (
                              <span className="text-sm font-medium text-foreground truncate">{a.title}</span>
                            )}
                          </div>
                          <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                            {a.createdDateTime && <span>{formatDate(a.createdDateTime)}</span>}
                            {a.createdDateTime && <span aria-hidden="true">·</span>}
                            {a.createdDateTime && <span>{formatTimeAgo(a.createdDateTime)}</span>}
                            {a.productName && <><span aria-hidden="true">·</span><span>{a.productName}</span></>}
                          </div>
                          {a.description && (
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{a.description}</p>
                          )}
                          {a.mitreTechniques && a.mitreTechniques.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {a.mitreTechniques.map((t, j) => (
                                <span key={`${j}-${t}`} className="text-[10px] bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded font-medium">{t}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}

          {(relatedLoading || (relatedIncidents && relatedIncidents.length > 0)) && (
            <div className="bg-muted/50 rounded-lg p-3 mb-4">
              <button
                type="button"
                onClick={() => setRelatedIncidentsOpen(v => !v)}
                className="w-full flex items-baseline justify-between mb-0 group"
                aria-expanded={relatedIncidentsOpen}
              >
                <p className="text-xs text-muted-foreground font-semibold flex items-center gap-1.5">
                  <svg
                    className={`w-3 h-3 shrink-0 transition-transform ${relatedIncidentsOpen ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  Related Incidents
                  {relatedIncidents && relatedIncidents.length > 0 && (
                    <span className="font-normal text-muted-foreground/70"> · {relatedIncidents.length} sharing entities (last 30d)</span>
                  )}
                </p>
                {relatedLoading && <span className="text-[10px] italic text-muted-foreground">searching…</span>}
              </button>
              {relatedIncidentsOpen && relatedIncidents && relatedIncidents.length > 0 && (
                <ul className="divide-y divide-border mt-2">
                  {relatedIncidents.map((r) => {
                    const rowContent = (
                      <>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${severityChipClass(r.severity)}`}>{r.severity || 'medium'}</span>
                          <span className="text-xs font-mono text-muted-foreground">#{r.incidentNumber}</span>
                          <span className="text-sm font-medium text-foreground truncate flex-1 min-w-0">{r.title}</span>
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2">
                          {r.status && <span>{r.status}</span>}
                          {r.createdTime && (<><span aria-hidden="true">·</span><span>{formatTimeAgo(r.createdTime)}</span></>)}
                          {r.sharedAlertCount > 0 && (<><span aria-hidden="true">·</span><span>{r.sharedAlertCount} shared alert{r.sharedAlertCount === 1 ? '' : 's'}</span></>)}
                        </div>
                      </>
                    );
                    if (onOpenIncident) {
                      return (
                        <li key={r.incidentNumber}>
                          <button
                            type="button"
                            onClick={() => onOpenIncident(r.incidentNumber)}
                            className="w-full text-left py-2 px-1 hover:bg-card/60 rounded transition"
                            aria-label={`Open related incident #${r.incidentNumber}: ${r.title}`}
                          >
                            {rowContent}
                          </button>
                        </li>
                      );
                    }
                    return (
                      <li key={r.incidentNumber} className="py-2 px-1">
                        {rowContent}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {(displayedEntities.length > 0 || enriching) && (() => {
            // Build the deduped IOC key set ONCE so attempted/resolved/flagged stay numerically
            // consistent (entities can repeat the same IOC value across different entity types).
            const seenIocKeys = new Set<string>();
            for (const e of displayedEntities) {
              const picked = pickIocFromEntity(e);
              if (!picked) continue;
              const k = picked.value.toLowerCase();
              if (seenIocKeys.has(k)) continue;
              seenIocKeys.add(k);
              if (seenIocKeys.size >= 25) break;
            }
            const repAttempted = seenIocKeys.size;
            // Roll up the auto-reputation results, counting each unique IOC once.
            let repFlagged = 0;
            let repClean = 0;
            let repResolved = 0;
            for (const key of seenIocKeys) {
              const rep = reputationMap.get(key);
              if (!rep) continue;
              repResolved++;
              if (rep.overallVerdict === 'malicious' || rep.overallVerdict === 'suspicious') repFlagged++;
              else if (rep.overallVerdict === 'clean') repClean++;
            }
            return (
            <div className="min-w-0 overflow-hidden bg-muted/50 rounded-lg p-3 mb-4">
              <div className="flex items-baseline justify-between gap-2 mb-2">
                <p className="text-xs text-muted-foreground font-semibold">Entities</p>
                {onRunKql && displayedEntities.length > 0 && (
                  <p className="text-[10px] text-muted-foreground/70 italic">click an entity to hunt across the data lake</p>
                )}
              </div>
              {/* Auto-reputation status line (only shown if at least one IOC was eligible) */}
              {repAttempted > 0 && (
                <div className="text-[10px] text-muted-foreground mb-2 flex items-center gap-2 flex-wrap">
                  {reputationLoading ? (
                    <span className="italic">enriching {repAttempted} IOC{repAttempted === 1 ? '' : 's'} across threat-intel providers…</span>
                  ) : reputationProvidersConfigured === 0 ? (
                    <span className="italic text-orange-600 dark:text-orange-400">
                      Threat-intel providers not configured. Add API keys in Settings, Threat Intel for automatic IOC enrichment.
                    </span>
                  ) : reputationError ? (
                    <span className="italic text-red-600 dark:text-red-400">TI enrichment failed: {reputationError}</span>
                  ) : (
                    <>
                      <span className="font-semibold uppercase tracking-wide">Threat-Intel</span>
                      <span>·</span>
                      <span>{repResolved}/{repAttempted} resolved</span>
                      {repFlagged > 0 && (
                        <span className="text-red-600 dark:text-red-400 font-semibold">· {repFlagged} flagged</span>
                      )}
                      {repClean > 0 && (
                        <span className="text-green-600 dark:text-green-400">· {repClean} clean</span>
                      )}
                      {repResolved === 0 && repAttempted > 0 && (
                        <span className="italic text-muted-foreground/80">· no provider could enrich these IOC types</span>
                      )}
                    </>
                  )}
                </div>
              )}
              {displayedEntities.length === 0 && enriching ? (
                <p className="text-xs italic text-muted-foreground">loading entities from Sentinel…</p>
              ) : (
              <div className="flex max-h-[260px] min-w-0 flex-wrap content-start gap-1.5 overflow-y-auto pr-1">
                {displayedEntities.map((e, i) => {
                  const name = getEntityName(e);
                  const sentinelVerdict = getEntityVerdict(e);
                  const type = getEntityType(e);
                  const picked = pickIocFromEntity(e);
                  const rep = picked ? reputationMap.get(picked.value.toLowerCase()) : undefined;
                  // Choose the more-severe verdict between Sentinel's tag and our reputation lookup.
                  const effectiveVerdict = rep
                    ? maxVerdict(sentinelVerdict as ReputationVerdict | undefined, rep.overallVerdict)
                    : sentinelVerdict;
                  const baseCls = `flex max-w-full min-w-0 flex-wrap items-center gap-1 rounded px-2 py-0.5 text-left font-mono text-xs ${verdictClass(effectiveVerdict)}`;
                  const repTip = rep
                    ? `Threat-intel verdict: ${rep.overallVerdict.toUpperCase()} (${rep.malicious + rep.suspicious}/${rep.total} flagged · ${rep.malicious}M ${rep.suspicious}S)\n` +
                      rep.results
                        .filter(r => r.ok || (r.error && r.error !== 'no_key' && r.error !== 'unsupported_type'))
                        .map(r => `· ${PROVIDER_LABEL[r.provider]}: ${r.ok ? r.verdict.toUpperCase() : 'error'}${r.summary ? ': ' + r.summary : ''}`)
                        .join('\n')
                    : '';
                  const geo = rep && picked?.type === 'ip' ? extractIpGeo(rep) : null;
                  const geoFlag = geo?.countryCode ? countryFlag(geo.countryCode) : '';
                  const tunnelType = geo?.isTor ? 'Tor' : geo?.isVpn ? 'VPN' : geo?.isProxy ? 'Proxy' : geo?.isHosting ? 'Hosting' : null;
                  const tunnelOp = geo?.tunnelLabel?.match(/(?:vpn|proxy|tor)\/([^,]+)/i)?.[1] ?? null;
                  const geoTip = [geo?.countryCode && `Country: ${countryName(geo.countryCode)}`, geo?.isp && `ISP: ${geo.isp}`, geo?.usageType && `Usage: ${geo.usageType}`, geo?.tunnelLabel && `Tunnel: ${geo.tunnelLabel}`].filter(Boolean).join('\n');
                  const content = (
                    <>
                      {type && <span className="shrink-0 opacity-60 text-[10px] uppercase font-semibold not-italic">{type}</span>}
                      <span className="min-w-0 break-all">{name}</span>
                      {effectiveVerdict && effectiveVerdict !== 'unknown' && (
                        <span className="shrink-0 opacity-70 text-[10px] font-semibold not-italic">[{effectiveVerdict}]</span>
                      )}
                      {rep && (
                        <span
                          className="shrink-0 text-[9px] font-bold not-italic px-1 rounded bg-black/10 dark:bg-white/10"
                          title="Threat-intel providers reporting this IOC as malicious/suspicious vs total queried"
                        >
                          TI {rep.malicious + rep.suspicious}/{rep.total}
                        </span>
                      )}
                      {geo?.countryCode && (
                        <span className="shrink-0 not-italic text-[11px]" title={geoTip}>{geoFlag ? `${geoFlag} ` : ''}{countryName(geo.countryCode)}</span>
                      )}
                      {geo && tunnelType && (
                        <span
                          className={`shrink-0 not-italic text-[9px] font-bold px-1 py-0.5 rounded ${geo.isTor ? 'bg-purple-500/20 text-purple-700 dark:text-purple-300' : geo.isVpn ? 'bg-amber-500/20 text-amber-700 dark:text-amber-300' : geo.isProxy ? 'bg-orange-500/20 text-orange-700 dark:text-orange-300' : 'bg-slate-500/15 text-slate-600 dark:text-slate-400'}`}
                          title={geoTip}
                        >
                          {tunnelType}{tunnelOp ? ` · ${tunnelOp}` : ''}
                        </span>
                      )}
                      {geo && !tunnelType && geo.isHosting && (
                        <span className="shrink-0 not-italic text-[9px] font-bold px-1 py-0.5 rounded bg-slate-500/15 text-slate-600 dark:text-slate-400" title={geoTip}>Hosting</span>
                      )}
                    </>
                  );
                  // Per-provider link pills. Only show providers that returned ok AND have a deep-link.
                  const providerLinks = rep
                    ? rep.results.filter(r => r.ok && r.link)
                    : [];
                  const chip = onRunKql ? (
                    <button
                      type="button"
                      onClick={() => { onRunKql(buildEntityHuntKql(e)); onClose(); }}
                      className={`${baseCls} hover:ring-2 hover:ring-primary/50 transition cursor-pointer`}
                      title={rep ? repTip : `Hunt for "${name}" across SecurityAlert (last 30d)`}
                      aria-label={`Hunt for entity ${name} across the data lake`}
                    >
                      {content}
                    </button>
                  ) : (
                    <span className={baseCls} title={repTip || undefined}>
                      {content}
                    </span>
                  );
                  return (
                    <span key={getEntityKey(e, i)} className="inline-flex max-w-full min-w-0 items-center gap-1 flex-wrap">
                      {chip}
                      {providerLinks.length > 0 && (
                        <span className="inline-flex items-center gap-0.5">
                          {providerLinks.map(r => (
                            <a
                              key={r.provider}
                              href={r.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(ev) => ev.stopPropagation()}
                              className={`text-[9px] font-bold not-italic px-1.5 py-0.5 rounded transition ${providerPillClass(r.verdict)}`}
                              title={`${PROVIDER_LABEL[r.provider]} · ${r.verdict.toUpperCase()}${r.summary ? ': ' + r.summary : ''} · click to open in ${PROVIDER_LABEL[r.provider]}`}
                              aria-label={`Open ${PROVIDER_LABEL[r.provider]} report for ${name}`}
                            >
                              {PROVIDER_SHORT[r.provider]} ↗
                            </a>
                          ))}
                        </span>
                      )}
                    </span>
                  );
                })}
              </div>
              )}
            </div>
            );
          })()}

          {/* Live SOC L1 streaming feed — auto-trigger on open. We keep the
              panel visible even after a cached short-circuit so the user
              isn't startled by the "starting…" line vanishing the moment the
              backend returns. The cached banner inside the panel points the
              user down to the AI Investigation result below. */}
          {(streamActive || streamEvents.some(isFeedWorthy) || streamError || streamCached) && (
            <LiveInvestigationFeed
              events={streamEvents}
              phase={streamPhase}
              active={streamActive}
              error={streamError}
              cached={streamCached}
            />
          )}

          {/* SOC L1 Auto-Investigation — autonomous RCA the scheduler posted to Sentinel */}
          {incident.autoInvestigation && !aiAnalysis && (
            <AutoInvestigationSection ai={incident.autoInvestigation} />
          )}

          {/* Agentic investigation loop (multi-step tool calling + destructive gate) */}
          {canRunAiInvestigation && (
            <InvestigatePanel
              incidentId={incident.id}
              incidentNumber={incident.incidentNumber}
              customerId={customerId}
              incidentSummary={incidentSummary}
              primaryUpn={primaryUpn}
              primaryHost={primaryHost}
              incident={incident}
              entities={displayedEntities}
              mitreTactics={displayedTactics}
              alertsCount={displayedAlertCount}
            />
          )}

          {/* AI Investigation result — only renders after a run */}
          {(aiLoading || aiError || aiAnalysis) && (
            <div className="bg-muted/50 rounded-lg p-3 mb-4">
              <p className="text-xs text-muted-foreground mb-2 font-semibold">AI Investigation</p>
              {aiLoading && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="animate-pulse">●</span>
                  <span>Reasoning over incident details, entities, and MITRE context… (typically 5-30 seconds)</span>
                </div>
              )}
              {aiError && !aiNoConfig && (
                <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded px-2 py-1.5 whitespace-pre-wrap break-words font-mono">
                  {aiError}
                </div>
              )}
              {aiNoConfig && (
                <div className="text-xs text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-950 border border-orange-200 dark:border-orange-800 rounded px-2 py-1.5">
                  Azure OpenAI is not configured yet. Open <strong>Settings → AI / Foundry</strong> to connect your Azure OpenAI endpoint and model deployment.
                </div>
              )}
              {aiVerdict && !aiLoading && (() => {
                // Colour the verdict banner by classification — red = TP
                // (operator should act), amber = BP (rule fired correctly
                // but activity is sanctioned), grey = FP (rule needs tuning).
                const palette =
                  aiVerdict.verdict === 'TRUE POSITIVE'
                    ? {
                        wrap: 'bg-red-50 dark:bg-red-950/40 border-red-300 dark:border-red-800',
                        text: 'text-red-700 dark:text-red-300',
                        chipBg: 'bg-red-600 text-white',
                        dot: 'bg-red-600',
                      }
                    : aiVerdict.verdict === 'BENIGN POSITIVE'
                      ? {
                          wrap: 'bg-amber-50 dark:bg-amber-950/40 border-amber-300 dark:border-amber-800',
                          text: 'text-amber-800 dark:text-amber-300',
                          chipBg: 'bg-amber-600 text-white',
                          dot: 'bg-amber-500',
                        }
                      : {
                          wrap: 'bg-slate-100 dark:bg-slate-800/60 border-slate-300 dark:border-slate-700',
                          text: 'text-slate-700 dark:text-slate-300',
                          chipBg: 'bg-slate-600 text-white',
                          dot: 'bg-slate-400',
                        };
                const confidenceBadge =
                  aiVerdict.confidence === 'High'
                    ? 'bg-emerald-600 text-white'
                    : aiVerdict.confidence === 'Medium'
                      ? 'bg-sky-600 text-white'
                      : 'bg-slate-500 text-white';
                return (
                  <div className={`mt-2 border rounded-lg p-3 ${palette.wrap}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-block h-2.5 w-2.5 rounded-full ${palette.dot}`} aria-hidden />
                      <span className={`text-[10px] font-bold tracking-wider px-2 py-0.5 rounded ${palette.chipBg}`}>
                        {aiVerdict.verdict}
                      </span>
                      <span className={`text-[10px] font-bold tracking-wider px-2 py-0.5 rounded ${confidenceBadge}`}>
                        {aiVerdict.confidence.toUpperCase()} CONFIDENCE
                      </span>
                      <span className="text-[10px] text-muted-foreground ml-auto">Neo SOC L1 verdict</span>
                    </div>
                    {aiVerdict.rationale && (
                      <p className={`mt-2 text-xs leading-relaxed ${palette.text}`}>
                        {aiVerdict.rationale}
                      </p>
                    )}
                  </div>
                );
              })()}
              {aiAnalysis && !aiLoading && (
                <div className="mt-2 bg-card border border-border rounded-lg p-3 max-h-[500px] overflow-y-auto">
                  <MarkdownView blocks={aiBlocks} onRunKql={handleRunKql} />
                </div>
              )}
            </div>
          )}

          {incident.classification && incident.classification !== 'unknown' && (
            <div className="bg-muted/50 rounded-lg p-3 mb-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Classification</p>
                  <p className="text-sm text-foreground">{formatLabel(incident.classification)}</p>
                </div>
                {incident.determination && incident.determination !== 'unknown' && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Determination</p>
                    <p className="text-sm text-foreground">{formatLabel(incident.determination)}</p>
                  </div>
                )}
              </div>
              {incident.classificationComment && (
                <div className="mt-2 pt-2 border-t border-border">
                  <p className="text-xs text-muted-foreground mb-1">Comment</p>
                  <p className="text-sm text-foreground">{incident.classificationComment}</p>
                </div>
              )}
            </div>
          )}

          {customerId && (
            <IncidentExecutionDetailsPanel incidentId={incident.id} customerId={customerId} />
          )}

          {/* Comments — bidirectional sync with Sentinel portal */}
          <div className="bg-muted/50 rounded-lg p-3 mb-4">
            <p className="text-xs text-muted-foreground mb-2 font-semibold">
              Comments {commentsLoading && <span className="font-normal italic">(loading…)</span>}
              {!commentsLoading && comments.length > 0 && <span className="font-normal text-muted-foreground/70"> · {comments.length}</span>}
            </p>
            {comments.length === 0 && !commentsLoading && (
              <p className="text-xs text-muted-foreground italic mb-2">No comments yet. Add one below to sync with Sentinel.</p>
            )}
            {comments.length > 0 && (
              <div className="space-y-2 mb-3 max-h-[200px] overflow-y-auto">
                {comments.map(c => (
                  <div key={c.id} className="bg-card border border-border rounded p-2">
                    <div className="flex justify-between items-baseline mb-1">
                      <span className="text-xs font-semibold text-foreground">{c.author}</span>
                      <span className="text-[10px] text-muted-foreground">{c.createdTime ? formatTimeAgo(c.createdTime) : ''}</span>
                    </div>
                    <p className="text-xs text-foreground whitespace-pre-wrap">{c.message}</p>
                  </div>
                ))}
              </div>
            )}
            {canComment && (
              <>
                <div className="flex gap-2">
                  <textarea
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    placeholder="Add a comment to sync with Sentinel portal…"
                    rows={2}
                    disabled={posting}
                    className="flex-1 text-xs border border-border rounded px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                  />
                  <button
                    onClick={() => postComment()}
                    disabled={posting || !newComment.trim()}
                    className="text-xs bg-primary text-primary-foreground px-3 py-1.5 rounded font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 self-start"
                  >
                    {posting ? 'Posting…' : 'Post'}
                  </button>
                </div>
                {postFallbackPrompt && (
                  <div className="mt-2 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 p-3">
                    <p className="text-xs text-amber-900 dark:text-amber-100 mb-2">{postFallbackPrompt}</p>
                    <div className="flex gap-2">
                      {postFallbackAllowed && (
                        <button
                          type="button"
                          onClick={() => postComment(true)}
                          disabled={posting}
                          className="px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold disabled:opacity-50"
                        >
                          Yes, use Service Principal
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => { setPostFallbackPrompt(null); setPostFallbackAllowed(false); }}
                        disabled={posting}
                        className="px-3 py-1.5 rounded border border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-100 text-xs font-semibold hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:opacity-50"
                      >
                        No, cancel
                      </button>
                    </div>
                  </div>
                )}
                {postError && <p className="text-xs text-red-500 mt-1">{postError}</p>}
              </>
            )}
          </div>

          {/* Per-customer Sentinel Logic-App playbooks. Renders nothing if the
              customer has no enabled playbooks configured, so it stays out of
              the way for tenants that haven't set anything up yet.
              Hidden entirely for SOC L1 (running playbooks is L2+). */}
          {canSeePlaybooksPanel && (
            <IncidentPlaybooksPanel
              incident={incident}
              customerId={customerId}
              readOnly={readOnly}
              entities={displayedEntities}
            />
          )}

          {/* ── Generate PDF Report ──────────────────────────────────────
               Restricted to admin / soc_manager / SOC L1 / SOC L2 (server
               enforces the same allow-list). The customer-facing portal
               and other roles never see this. Hidden when there's no
               customerId in scope. */}
          {customerId && canGenerateReport && (
            <div className="flex gap-2 mt-2 flex-wrap items-stretch">
              <button
                onClick={async () => {
                  if (!customerId) return;
                  setReportLoading(true);
                  setReportError(null);
                  try {
                    const payload = await apiFetch<IncidentReportPayload>(
                      `/incidents/${encodeURIComponent(incident.id)}/report`,
                      {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ customerId, outputType: REPORT_OUTPUT_TYPE }),
                      },
                    );
                    const exp = await exportIncidentReportPdf(payload, {
                      customerName: payload.customerName ?? customerId,
                      incidentTitle: incident.title,
                      incidentSeverity: incident.severity,
                      incidentStatus: incident.status,
                      incidentCreatedAt: incident.createdTime,
                    });
                    if (!exp.ok) setReportError(exp.error ?? 'PDF generation failed');
                  } catch (e) {
                    if (e instanceof ApiError) {
                      if (e.status === 409) setReportError('Run Triage or Deep Investigate first so the report has analyst notes to ground itself in.');
                      else if (e.forbidden) setReportError('Your role is not permitted to generate this report.');
                      else if (e.circuitOpen) setReportError('AI service is cooling down. Try again in a minute.');
                      else setReportError(e.message || 'Report generation failed');
                    } else {
                      setReportError(e instanceof Error ? e.message : String(e));
                    }
                  } finally {
                    setReportLoading(false);
                  }
                }}
                disabled={reportLoading}
                title="Generate a branded PDF report for this incident using AI + analyst notes"
                className="flex-1 min-w-[180px] bg-blue-600 text-white py-2.5 rounded-lg font-semibold text-sm hover:bg-blue-700 transition-colors disabled:opacity-60"
              >
                {reportLoading ? 'Generating…' : 'Generate Report'}
              </button>
            </div>
          )}
          {reportError && (
            <p className="text-xs text-red-500 mt-1" role="alert">{reportError}</p>
          )}

          <div data-ks-incident-modal-actions="true" className="flex gap-3 mt-2 flex-wrap">
            {onSyncIncident && (
              <button
                onClick={onSyncIncident}
                disabled={syncBusy}
                title="Fetch this incident directly from Microsoft Sentinel now, bypassing Log Analytics ingestion delay"
                className="flex-1 min-w-[180px] bg-slate-600 text-white py-2.5 rounded-lg font-semibold text-sm hover:bg-slate-700 transition-colors disabled:opacity-60"
              >
                {syncBusy ? 'Syncing…' : 'Sync with Microsoft Sentinel'}
              </button>
            )}
            {/* Deep Investigate — replaces the legacy Cross-alert summary
                surface. Opens the SentinelIR-Agent drawer (shared with the
                IncidentsTab "Deep Analysis" button). Hidden when there's no
                customerId in scope (the agent requires a customer context
                server-side) and when the matrix denies incidents.write
                (parent derives `readOnly` from canWrite, which is also what
                the server-side `requireTabWrite("incidents")` enforces). */}
            {customerId && canRunAiInvestigation && user?.role !== 'soc_l1' && (
              <button
                onClick={() => setDeepDrawerOpen(true)}
                title="Run a Tier-2/Tier-3 deep investigation (SentinelIR-Agent) - multi-turn KQL hunt with structured report"
                className="flex-1 min-w-[180px] bg-violet-600 text-white py-2.5 rounded-lg font-semibold text-sm hover:bg-violet-700 transition-colors"
              >
                Deep Investigate
              </button>
            )}
            {canRunAiInvestigation && (
              <button onClick={() => { void runAiInvestigation({ force: true }); }} disabled={aiLoading}
                className="flex-1 min-w-[180px] bg-indigo-600 text-white py-2.5 rounded-lg font-semibold text-sm hover:bg-indigo-700 transition-colors disabled:opacity-60">
                {aiLoading ? 'Analyzing…' : aiAnalysis ? 'Re-analyze with AI' : 'Investigate with AI'}
              </button>
            )}
            {customerId && (
              <AskCopilotButton
                customerId={customerId}
                incidentId={incident.id}
                incidentTitle={incident.title}
                incidentContext={{
                  incidentId: incident.id,
                  incidentNumber: incident.incidentNumber,
                  title: incident.title,
                  severity: incident.severity,
                  status: incident.status,
                  entities: incident.entities,
                  tactics: incident.mitreTactics,
                }}
              />
            )}
            {customerId && canViewExplainability && (
              <button
                type="button"
                onClick={() => setExplainabilityOpen(prev => !prev)}
                title="Explain why Sentinel/Defender created this incident: rule logic, matching entities, source tables, triggering rows, grouping reason and tuning options"
                className={`flex-1 min-w-[180px] text-white py-2.5 rounded-lg font-semibold text-sm transition-colors ${explainabilityOpen ? 'bg-violet-700 hover:bg-violet-800' : 'bg-violet-600 hover:bg-violet-700'}`}
              >
                {explainabilityOpen ? 'Hide Why Created' : 'Why Was This Created?'}
              </button>
            )}
            {customerId && canSimulateResponse && (
              <button
                type="button"
                onClick={() => setWhatIfOpen(prev => !prev)}
                title="Simulate containment options such as isolate device, disable account, block IoC, revoke sessions or reset password before execution"
                className={`flex-1 min-w-[180px] text-white py-2.5 rounded-lg font-semibold text-sm transition-colors ${whatIfOpen ? 'bg-purple-700 hover:bg-purple-800' : 'bg-purple-600 hover:bg-purple-700'}`}
              >
                {whatIfOpen ? 'Hide Incident What-If' : 'Incident What-If'}
              </button>
            )}
            {customerId && canViewSlaPrediction && (
              <button
                type="button"
                onClick={() => setSlaPredictionOpen(prev => !prev)}
                title="Predict SLA breach risk before the incident breaches its policy"
                className={`flex-1 min-w-[180px] text-white py-2.5 rounded-lg font-semibold text-sm transition-colors ${slaPredictionOpen ? 'bg-amber-700 hover:bg-amber-800' : 'bg-amber-600 hover:bg-amber-700'}`}
              >
                {slaPredictionOpen ? 'Hide SLA Predictor' : 'SLA Predictor'}
              </button>
            )}
            {customerId && canUseAutopilot && (
              <button
                type="button"
                onClick={() => setAutopilotOpen(prev => !prev)}
                title="Generate SOC Copilot Autopilot recommendations with human approval, containment impact and rollback guidance"
                className={`flex-1 min-w-[180px] text-white py-2.5 rounded-lg font-semibold text-sm transition-colors ${autopilotOpen ? 'bg-blue-700 hover:bg-blue-800' : 'bg-blue-600 hover:bg-blue-700'}`}
              >
                {autopilotOpen ? 'Hide SOC Autopilot' : 'SOC Autopilot'}
              </button>
            )}
            {customerId && canGenerateNextBestQueries && (
              <button
                type="button"
                onClick={() => setNextBestQueryOpen(prev => !prev)}
                title="Generate 5–10 context-aware Sentinel and Defender KQL pivots for this incident"
                className={`flex-1 min-w-[180px] text-white py-2.5 rounded-lg font-semibold text-sm transition-colors ${nextBestQueryOpen ? 'bg-indigo-700 hover:bg-indigo-800' : 'bg-indigo-600 hover:bg-indigo-700'}`}
              >
                {nextBestQueryOpen ? 'Hide Next Best Queries' : 'Next Best Queries'}
              </button>
            )}
            {customerId && canGenerateCustomerUpdate && (
              <button
                type="button"
                onClick={() => setCustomerUpdateOpen(prev => !prev)}
                title="Generate a customer-safe incident update with status, impact, actions taken, next steps and ETA"
                className={`flex-1 min-w-[180px] text-white py-2.5 rounded-lg font-semibold text-sm transition-colors ${customerUpdateOpen ? 'bg-emerald-700 hover:bg-emerald-800' : 'bg-emerald-600 hover:bg-emerald-700'}`}
              >
                {customerUpdateOpen ? 'Hide Customer Update' : 'Customer Update'}
              </button>
            )}
            {customerId && canUseGuidedTriage && (
              <button
                type="button"
                onClick={() => setGuidedTriageOpen(prev => !prev)}
                title="Open the guided triage workbench for a repeatable SOC investigation flow"
                className={`flex-1 min-w-[180px] text-white py-2.5 rounded-lg font-semibold text-sm transition-colors ${guidedTriageOpen ? 'bg-sky-700 hover:bg-sky-800' : 'bg-sky-600 hover:bg-sky-700'}`}
              >
                {guidedTriageOpen ? 'Hide Guided Triage' : 'Guided Triage'}
              </button>
            )}
            {customerId && canViewVulnerabilityCorrelation && (
              <button
                type="button"
                onClick={() => setVulnerabilityCorrelationOpen(prev => !prev)}
                title="Correlate affected incident devices with Defender Vulnerability Management exposure and exploit context"
                className={`flex-1 min-w-[180px] text-white py-2.5 rounded-lg font-semibold text-sm transition-colors ${vulnerabilityCorrelationOpen ? 'bg-orange-700 hover:bg-orange-800' : 'bg-orange-600 hover:bg-orange-700'}`}
              >
                {vulnerabilityCorrelationOpen ? 'Hide Vuln Correlation' : 'Vuln Correlation'}
              </button>
            )}
            {customerId && canFindSimilarIncidents && (
              <button
                type="button"
                onClick={() => setSimilarIncidentsOpen(prev => !prev)}
                title="Find similar historical incidents and compare how they were resolved"
                className={`flex-1 min-w-[180px] text-white py-2.5 rounded-lg font-semibold text-sm transition-colors ${similarIncidentsOpen ? 'bg-rose-700 hover:bg-rose-800' : 'bg-rose-600 hover:bg-rose-700'}`}
              >
                {similarIncidentsOpen ? 'Hide Similar Incidents' : 'Similar Incidents'}
              </button>
            )}
            {customerId && canViewAttackStory && (
              <button
                type="button"
                onClick={() => setAttackStoryOpen(prev => !prev)}
                title="Fetch and display Sentinel / Defender XDR attack story, timeline and investigation graph for this incident"
                className={`flex-1 min-w-[180px] text-white py-2.5 rounded-lg font-semibold text-sm transition-colors ${attackStoryOpen ? 'bg-fuchsia-700 hover:bg-fuchsia-800' : 'bg-fuchsia-600 hover:bg-fuchsia-700'}`}
              >
                {attackStoryOpen ? 'Hide Attack Story' : 'Attack Story / Graph'}
              </button>
            )}
            {canOpenCaseEvidencePanel && (
              <button
                type="button"
                onClick={() => setCaseEvidenceOpen(prev => !prev)}
                title="Upload and manage screenshots, Word/PDF files, exports and investigation artifacts for this incident"
                className={`flex-1 min-w-[180px] text-white py-2.5 rounded-lg font-semibold text-sm transition-colors ${caseEvidenceOpen ? 'bg-cyan-700 hover:bg-cyan-800' : 'bg-cyan-600 hover:bg-cyan-700'}`}
              >
                {caseEvidenceOpen ? 'Hide Case Evidence' : 'Case Evidence'}
              </button>
            )}
            {!isClosed && canCloseIncident && (
              <button onClick={onCloseIncident}
                className="flex-1 bg-green-600 text-white py-2.5 rounded-lg font-semibold text-sm hover:bg-green-700 transition-colors">
                Close Incident
              </button>
            )}
            {onStatusIncident && ((isClosed && canReopenIncident) || (!isClosed && canChangeIncidentStatus)) && (
              <button onClick={() => onStatusIncident()}
                className="flex-1 bg-blue-600 text-white py-2.5 rounded-lg font-semibold text-sm hover:bg-blue-700 transition-colors">
                {isClosed ? 'Reopen Incident' : 'Change Status'}
              </button>
            )}

          </div>

          {customerId && canViewExplainability && explainabilityOpen && (
            <IncidentExplainabilityPanel
              incident={incident}
              customerId={customerId}
              onRunKql={handleRunKql}
            />
          )}

          {customerId && canSimulateResponse && whatIfOpen && (
            <IncidentWhatIfPanel
              incident={incident}
              customerId={customerId}
            />
          )}

          {customerId && canViewSlaPrediction && slaPredictionOpen && (
            <IncidentSlaPredictionPanel
              incident={incident}
              customerId={customerId}
            />
          )}

          {customerId && canUseAutopilot && autopilotOpen && (
            <IncidentAutopilotPanel
              incident={incident}
              customerId={customerId}
            />
          )}

          {customerId && canGenerateNextBestQueries && nextBestQueryOpen && (
            <NextBestQueryPanel
              customerId={customerId}
              context={{
                kind: 'incident',
                customerId,
                incidentId: getIncidentEventsId(),
                incidentNumber: incident.incidentNumber,
                title: incident.title,
                severity: incident.severity,
                provider: incident.providerName,
                lookback: '30d',
                extra: { entities: displayedEntities, tactics: displayedTactics, alertCount: displayedAlertCount },
              }}
              onRunKql={handleRunKql}
              incidentRef={{ incidentId: getIncidentEventsId(), incidentNumber: incident.incidentNumber, title: incident.title }}
            />
          )}

          {customerId && canGenerateCustomerUpdate && customerUpdateOpen && (
            <CustomerUpdateGeneratorPanel
              incident={incident}
              customerId={customerId}
            />
          )}

          {customerId && canUseGuidedTriage && guidedTriageOpen && (
            <GuidedTriagePanel
              incident={incident}
              customerId={customerId}
              onRunKql={handleRunKql}
            />
          )}

          {customerId && canViewVulnerabilityCorrelation && vulnerabilityCorrelationOpen && (
            <IncidentVulnerabilityCorrelationPanel
              incident={incident}
              customerId={customerId}
            />
          )}

          {customerId && canFindSimilarIncidents && similarIncidentsOpen && (
            <IncidentSimilarityPanel
              incident={incident}
              customerId={customerId}
              onOpenIncident={onOpenIncident}
            />
          )}

          {customerId && canViewAttackStory && attackStoryOpen && (
            <AttackStoryPanel
              incident={incident}
              customerId={customerId}
              canAnalyze={canAnalyzeAttackStory}
              canReplay={canViewAttackReplay}
              onRunKql={handleRunKql}
            />
          )}

          {canOpenCaseEvidencePanel && caseEvidenceOpen && (
            <div data-ks-case-evidence-react-panel="true" className="mt-3 rounded-xl border border-cyan-200 dark:border-cyan-900 bg-cyan-50/60 dark:bg-cyan-950/20 p-3">
              <div className="mb-2 flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-bold text-cyan-900 dark:text-cyan-100">Case Evidence</p>
                  <p className="text-xs text-cyan-800/80 dark:text-cyan-200/80">Attach SOC evidence directly against this incident. Files are stored in the configured evidence repository and indexed with SHA256 metadata.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setCaseEvidenceOpen(false)}
                  className="rounded-lg border border-cyan-300 dark:border-cyan-800 px-2.5 py-1.5 text-xs font-semibold text-cyan-800 dark:text-cyan-100 hover:bg-cyan-100 dark:hover:bg-cyan-900/40"
                >
                  Collapse
                </button>
              </div>
              {!customerId ? (
                <div className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs text-amber-800 dark:text-amber-200">
                  Case Evidence is available, but this incident modal does not currently have a customer context. Open the incident from a selected customer workspace, then try again.
                </div>
              ) : canSeeCaseEvidence ? (
                <CaseEvidencePanel
                  customerId={customerId}
                  incidentId={getIncidentEventsId()}
                  incidentNumber={incident.incidentNumber}
                  title={incident.title}
                  incident={incident as unknown as Record<string, unknown>}
                  armIncidentName={incident.armIncidentName}
                  graphIncidentId={incident.graphIncidentId}
                  providerName={incident.providerName}
                />
              ) : (
                <div className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs text-amber-800 dark:text-amber-200">
                  Case Evidence is available as an incident action, but your current effective permission cache does not include <span className="font-semibold">Case Evidence → Read</span>. Re-save the role in Permissions Master or refresh the session. Backend routes will still enforce upload/download permissions.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Deep Investigate drawer — slide-in panel that runs the multi-turn
          SentinelIR-Agent KQL hunt and renders the structured Investigation
          Report. Renders OUTSIDE the modal's centered card but inside the
          fixed-position overlay, so its own fixed/inset-0 positioning still
          covers the screen and z-[1000] keeps it above the modal chrome.
          Gated by `!readOnly` so users without incidents.write on this
          customer can never reach the spend (mirrors the server-side
          `requireTabWrite("incidents")` for defence in depth). */}
      {deepDrawerOpen && customerId && canRunAiInvestigation && user?.role !== 'soc_l1' && (
        <DeepInvestigationDrawer
          incident={incident}
          customerId={customerId}
          readOnly={readOnly}
          onClose={() => setDeepDrawerOpen(false)}
        />
      )}
    </div>
  );
}
